import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { sanitizeButtonLabel } from '../../../common/utils/sanitize-html';
import { useTranslation } from 'react-i18next';
import {
    ArrowDownToLine,
    ArrowUpToLine,
    Box,
    Check,
    ChevronDown,
    Copy,
    ExternalLink,
    Eye,
    EyeOff,
    Hash,
    ImageOff,
    Layers,
    Link2,
    ListEnd,
    ListStart,
    Loader2,
    Lock,
    Maximize2,
    Monitor,
    MousePointerClick,
    MoveHorizontal,
    Pencil,
    Plus,
    RefreshCw,
    Rotate3d,
    Rows3,
    Settings,
    Smartphone,
    Sofa,
    Wand2,
    X,
    Zap,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PageContainer } from '../../../common/components/PageContainer';
import { useNavigate } from 'react-router-dom';
import { useTabParam } from '../../../common/hooks/use-tab-param.hook';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { AutosaveReporterContext } from '../../../common/hooks/use-autosave.hook';
import { CanvasPortal } from './studio-canvas';
import {
    SettingsChoiceList,
    SettingsColorField,
    SettingsField,
    SettingsGroup,
    SettingsLabel,
    SettingsToggleRow,
} from '../../../components/design-system/common/SettingsPanel';
import { LivePagePlacer } from './live-page-placer';
import { ShopperMeasurementsSetting } from '../../../modules/vendor/settings/components/ShopperMeasurementsSetting';
import { PlacementAssistant } from './placement-assistant';
import { useSpacesApi } from '../../../common/hooks/use-spaces-api.hook';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { routes } from '../../../assets/constants';
import { ApiMethod } from '../../../common/models/api-types.type';
import {
    IntegrationConfig,
    IntegrationButtonConfig,
    FeatureBlock,
    FeatureKey,
    LayoutMode,
    VisualizerSettings,
    ArSettings,
    Model3DSettings,
    VisualizerOpenMode,
    VisualizerSurfaceMode,
} from '../../../modules/vendor/settings/models/config.model';
import {
    migrateIntegrationToV2 as migrateIntegrationToV2External,
    buildDefaultFeatures as buildDefaultFeaturesExternal,
    defaultInlineEmbedSettings,
    toLegacyOpenMode,
} from '../../../modules/vendor/settings/models/integration-migration';
import {
    isIntegrationEnabled,
    isWebsiteConnected,
} from '../../../modules/vendor/settings/models/integration-setup';

/** /visualizer/integration?tab=features — the visualizer's own settings; connecting the website is on Home. */
const INTEGRATION_TABS = ['matching', 'features', 'button'] as const;

// Default integration config
const DEFAULT_INTEGRATION: IntegrationConfig = {
    isEnabled: false,
    allowedDomains: [],
    matchStrategy: 'jsonld',
    matchConfig: { jsonldField: 'sku' },
    urlMatchingEnabled: false,
    urlMatchField: 'sku',
    button: {
        containerSelector: '#visio-ardealy',
        insertPosition: 'append',
        buttonStyle: 'filled',
        label: 'Visualize in your space',
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        borderColor: '#3B82F6',
        borderRadius: '8px',
        paddingH: '24',
        paddingV: '12',
        iconEnabled: true,
        iconOnly: false,
        iconSvg: '',
        fullWidth: false,
        customCSS: '',
    },
    openMode: 'iframe_overlay',
};

// Re-export the pure migration helpers so callers that already import from
// integration.page keep working (and for back-compat with any existing tests).
// The source of truth lives at ../../../modules/vendor/settings/models/integration-migration.
export const buildDefaultFeatures = buildDefaultFeaturesExternal;
export const migrateIntegrationToV2 = migrateIntegrationToV2External;


/**
 * "Point at it on your own page" — the element picker in a tab of its own.
 *
 * The gallery panel's container is picked this way; the button is placed on the
 * studio's canvas instead. It owns no state: the URL and the waiting flag live
 * in the tab, which also decides where a picked selector is written.
 */

/** Canonical surface name for whatever spelling a room or product carries. */
const canonicalSurface = (value: any): string | null => {
    const raw = String(value ?? '').trim().toLowerCase();
    if (raw === 'floor' || raw === 'floors') return 'floor';
    if (raw === 'wall' || raw === 'walls') return 'walls';
    return null;
};

/** The surfaces a room was analysed for, de-duplicated and canonical. */
const roomSurfaceList = (space: any): string[] => {
    const declared = Array.isArray(space?.surfaces) ? space.surfaces : [];
    return declared
        .map(canonicalSurface)
        .filter((surface, i, all) => surface && all.indexOf(surface) === i) as string[];
};

/**
 * Pick a room from the vendor's library, by looking at it.
 *
 * A dropdown of room names asks the vendor to remember which photo "Cuisine 2"
 * is, and — worse — tells them nothing about the one thing that decides whether
 * the choice works at all: whether the analysis found the surface this slot
 * needs. So the trigger shows the room, and the grid shows the library, with
 * every room labelled by its surfaces and the ones that cannot serve this slot
 * demoted to the end under their own heading.
 *
 * They stay selectable. A room's analysis can be re-run, and a vendor who knows
 * their library better than we do should not be blocked by it — the warning
 * under the trigger is the honest way to say it.
 */
const RoomPicker = ({ label, value, onChange, spaces, requiredSurface, testId, k }) => {
    const [open, setOpen] = useState(false);

    /**
     * A room is offered for this slot only if the analysis found the surface in
     * it. One that did not applies the product to nothing and renders the
     * untouched photo, so listing it — even dimmed — is offering a dead end.
     *
     * The room already selected is the exception: it stays visible whatever its
     * surfaces, so a slot filled before the split (or by a room whose analysis
     * has since changed) shows what it is set to instead of looking empty.
     */
    const serves = (space: any) => roomSurfaceList(space).includes(requiredSurface);
    const surfaceName = (surface: string) => k(surface === 'walls' ? 'SURFACE_WALLS' : 'SURFACE_FLOOR');
    const roomLabel = (space: any) => space?.name || space?.spaceType || space?.id;

    const selected = spaces.find((sp: any) => sp.id === value);
    // A room that was nominated and has since left the vendor's library — or was
    // never in it. The panel gets a 401 for it and quietly opens something else,
    // so this is the only place it can be said out loud.
    const missing = !!value && !selected;

    const usable = spaces.filter((sp: any) => serves(sp) || sp.id === value);

    /** The room's photo, or a plain block when the library has none for it. */
    const Thumb = ({ space, className, style }: any) => (
        space?.thumbnailPath
            ? <img src={space.thumbnailPath} alt="" loading="lazy" style={style} className={cn('object-cover', className)} />
            : (
                <div style={style} className={cn('flex items-center justify-center bg-muted text-muted-foreground', className)}>
                    <ImageOff className="size-4" />
                </div>
            )
    );

    const Tile = ({ space }: any) => {
        const isSelected = space.id === value;
        const surfaces = roomSurfaceList(space);
        return (
            <button
                type="button"
                data-testid={`inline-room-tile-${space.id}`}
                aria-pressed={isSelected}
                // The room's name is a generated filename — '7162_Chene_London_RS'
                // — which identifies nothing at a glance and crowds out the photo
                // that does. Kept where it still helps: the tooltip, the accessible
                // name, and the search box.
                title={roomLabel(space)}
                aria-label={roomLabel(space)}
                onClick={() => { onChange(space.id); setOpen(false); }}
                className={cn(
                    'group overflow-hidden rounded-lg border text-left transition',
                    isSelected ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground/40',
                )}
            >
                {/* Height inline, not by class: the Tailwind sheet is generated at
                    build time, and an image in a zero-height box falls back to its
                    natural size — which is a full-bleed photo, not a thumbnail. */}
                <div className="relative w-full bg-muted" style={{ height: 96 }}>
                    <Thumb space={space} className="size-full" />
                    {isSelected && (
                        <span className="absolute right-1 top-1 rounded-full bg-foreground p-0.5 text-background">
                            <Check className="size-3" />
                        </span>
                    )}
                </div>
                <div className="truncate px-1.5 py-1 text-[10px] text-muted-foreground">
                    {surfaces.length ? surfaces.map(surfaceName).join(' · ') : k('DEFAULT_SPACE_UNANALYSED')}
                </div>
            </button>
        );
    };

    return (
        <div data-testid={testId}>
            <div className="mb-1.5"><SettingsLabel>{label}</SettingsLabel></div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid={`${testId}-trigger`}
                        title={selected ? roomLabel(selected) : undefined}
                        className="w-full overflow-hidden rounded-lg border border-border bg-background text-left transition hover:border-foreground/40"
                    >
                        {selected
                            ? (
                                /* Nothing but the room. The field's own label already
                                   says which surface this slot is for, so repeating
                                   the room's surfaces underneath reads as a second,
                                   contradictory answer to the same question. */
                                <span className="relative block">
                                    <Thumb space={selected} className="w-full" style={{ height: 120 }} />
                                    <span className="absolute bottom-1 right-1 rounded-md bg-background/90 p-0.5 text-muted-foreground">
                                        <ChevronDown className="size-4" />
                                    </span>
                                </span>
                            )
                            : (
                                <span className="flex w-full items-center gap-2 p-1.5">
                                    <span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
                                        <Wand2 className="size-4" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-xs">{k('DEFAULT_SPACE_NONE')}</span>
                                        <span className="block truncate text-[10px] text-muted-foreground">
                                            {k('DEFAULT_SPACE_NONE_HINT')}
                                        </span>
                                    </span>
                                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                                </span>
                            )}
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-2" style={{ width: 'min(28rem, 80vw)' }}>
                    <div className="max-h-[19rem] overflow-y-auto pr-1">
                        <button
                            type="button"
                            data-testid={`${testId}-none`}
                            onClick={() => { onChange(''); setOpen(false); }}
                            className={cn(
                                'mb-2 flex w-full items-center gap-2 rounded-lg border border-dashed p-2 text-left text-xs transition',
                                value ? 'border-border hover:border-foreground/40' : 'border-foreground',
                            )}
                        >
                            <Wand2 className="size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0">
                                <span className="block truncate">{k('DEFAULT_SPACE_NONE')}</span>
                                <span className="block truncate text-[10px] text-muted-foreground">{k('DEFAULT_SPACE_NONE_HINT')}</span>
                            </span>
                        </button>

                        {usable.length === 0 && (
                            <p className="px-1 py-4 text-center text-xs text-muted-foreground">{k('DEFAULT_SPACE_EMPTY')}</p>
                        )}

                        {usable.length > 0 && (
                            <div className="grid grid-cols-3 gap-2">
                                {usable.map((sp: any) => <Tile key={sp.id} space={sp} />)}
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
            {missing && <p className="mt-1 text-xs text-warning">{k('DEFAULT_SPACE_NOT_IN_LIBRARY')}</p>}
        </div>
    );
};

/**
 * How the visualizer reaches a shopper: a button, the in-gallery widget, or both.
 *
 * Both was once refused, when the button only scrolled down to the widget — two
 * doors into the same room. It came back because vendors asked for it with a
 * different job for each: the widget in the gallery, a button by add-to-cart
 * that opens the same room full screen (ARDE-285).
 *
 * Two halves side by side, each drawn rather than described, each switched on
 * and off on its own. One always stays on: a visualizer reached by neither is
 * the feature switched off, which is its card's tick, not this. The difference
 * between them is visual — one is a pill you press, the other is the room
 * sitting in the gallery where a product photo would be — so the pictures do
 * the explaining.
 */
const VisualizerSurfaces = ({ surface, onSurface, openMode, onOpenMode, t }) => {
    const v = (suffix: string) => t(`INTEGRATION.FEATURES.VISUALIZER.${suffix}`);
    const on = { button: surface !== 'widget', widget: surface === 'widget' || surface === 'both' };

    const toggle = (value: 'button' | 'widget') => {
        const next = { ...on, [value]: !on[value] };
        if (!next.button && !next.widget) return;
        onSurface(next.button && next.widget ? 'both' : next.widget ? 'widget' : 'button');
    };

    /* A product page with a button on it. Its pill lights up when chosen. */
    const ButtonArt = ({ on }) => (
        <div className="flex h-12 w-16 gap-1 rounded-md border bg-background p-1.5">
            <div className="w-4 shrink-0 rounded-sm bg-muted" />
            <div className="flex flex-1 flex-col justify-center gap-0.5">
                <div className="h-0.5 w-full rounded-full bg-muted" />
                <div className="h-0.5 w-3/4 rounded-full bg-muted" />
                <div
                    className={cn(
                        'mt-0.5 h-2 rounded-full transition-all duration-300',
                        on ? 'w-full bg-foreground' : 'w-2/3 bg-muted-foreground/40',
                    )}
                />
            </div>
        </div>
    );

    /* The same page with the room in the gallery. The stage fills in when chosen. */
    const WidgetArt = ({ on }) => (
        <div className="flex h-12 w-16 gap-1 rounded-md border bg-background p-1.5">
            <div
                className={cn(
                    'flex flex-1 items-center justify-center rounded-sm transition-all duration-300',
                    on ? 'border border-foreground/50 bg-muted' : 'bg-muted/60',
                )}
            >
                <Sofa className={cn('transition-all duration-300', on ? 'size-4 text-foreground' : 'size-3 text-muted-foreground/50')} />
            </div>
            <div className="flex w-2 shrink-0 flex-col gap-0.5">
                <div className={cn('h-2 rounded-[2px] transition-colors duration-300', on ? 'border border-foreground/50 bg-muted' : 'bg-muted')} />
                <div className="h-2 rounded-[2px] bg-muted" />
                <div className="h-2 rounded-[2px] bg-muted" />
            </div>
        </div>
    );

    const Half = ({ value, art: Art, title, desc }) => {
        const checked = on[value];
        return (
            <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                data-testid={`surface-${value}`}
                onClick={() => toggle(value)}
                className={cn(
                    'flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border p-3 text-center transition-all duration-300',
                    checked ? 'border-foreground/25 bg-muted/50' : 'border-input opacity-70 hover:opacity-100',
                )}
            >
                <Art on={checked} />
                <span className="text-sm text-foreground">{title}</span>
                <span className="text-xs leading-snug text-muted-foreground">{desc}</span>
            </button>
        );
    };

    return (
        <SettingsField label={v('SURFACES')} hint={v('SURFACES_HINT')}>
            <div role="group" aria-label={v('SURFACES')} className="flex gap-2">
                <Half value="button" art={ButtonArt} title={v('SURFACE_BUTTON')} desc={v('SURFACE_BUTTON_DESC')} />
                <Half value="widget" art={WidgetArt} title={v('SURFACE_WIDGET')} desc={v('SURFACE_WIDGET_DESC')} />
            </div>

            {/*
                What the chosen surfaces still need, unfolding under them. Animated
                open so the strip reads as belonging to the halves above it rather
                than as the page jumping.
            */}
            <div
                className="grid transition-all duration-300 ease-out"
                style={{ gridTemplateRows: '1fr' }}
                data-testid={`surface-options-${surface}`}
            >
                <div className="overflow-hidden">
                    <div className="animate-in fade-in slide-in-from-top-1 duration-300 flex flex-col gap-2 pt-3">
                        {on.button && (
                            <ToggleGroup
                                type="single"
                                variant="outline"
                                size="sm"
                                className="w-full"
                                value={openMode || 'iframe_overlay'}
                                onValueChange={(value) => value && onOpenMode(value)}
                            >
                                <ToggleGroupItem value="iframe_overlay" className="flex-1 text-xs">
                                    {v('OPEN_MODE_IFRAME')}
                                </ToggleGroupItem>
                                <ToggleGroupItem value="new_tab" className="flex-1 text-xs">
                                    {v('OPEN_MODE_NEW_TAB')}
                                </ToggleGroupItem>
                            </ToggleGroup>
                        )}
                        {on.widget && (
                            <p className="text-xs text-muted-foreground">{v('INLINE.CONFIGURE_IN_BUTTON')}</p>
                        )}
                    </div>
                </div>
            </div>
        </SettingsField>
    );
};

/**
 * The widget's own settings, beside the page it will sit in.
 *
 * It began as a picture of the panel with the fields underneath, which was a
 * second preview competing with the real one: the page above already draws the
 * panel where it will land, at the shape it will have. What is left is the
 * short list of things a vendor decides — the words on it, the shape, when it
 * loads — and the shape is drawn rather than named, because "3:4" is a
 * rectangle, not a number.
 *
 * The three readings at the bottom are corrections, not choices: what the pick
 * concluded about the merchant's markup, editable for the gallery it read
 * wrongly. They stay folded, because a vendor who never opens them has not
 * skipped a decision.
 */
export const WidgetBuilder = ({ settings, onChange, t }) => {
    const s = { ...defaultInlineEmbedSettings(), ...(settings || {}) };
    const k = (suffix: string) => t(`INTEGRATION.FEATURES.VISUALIZER.INLINE.${suffix}`);

    /** Grouped choices as icon tiles, the house pattern for a small closed set. */
    const TileChoice = ({ value, options, onSelect }) => (
        <div className="flex flex-wrap gap-1.5">
            {options.map(({ key: optionKey, Icon, label }) => {
                const selected = value === optionKey;
                return (
                    <button
                        key={optionKey}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onSelect(optionKey)}
                        className={cn(
                            'flex flex-1 basis-24 cursor-pointer flex-col items-center gap-1 rounded-lg border px-2 py-2 transition-colors',
                            selected ? 'border-foreground/25 bg-muted/60' : 'border-input hover:bg-muted/40',
                        )}
                    >
                        <Icon className="size-4 text-muted-foreground" />
                        <span className="text-[11px] leading-tight text-foreground">{label}</span>
                    </button>
                );
            })}
        </div>
    );

    /* The shapes, drawn. Sized so the tallest and the widest both sit in one
       row without the row growing to hold them. */
    const RATIOS = [
        { value: '1:1', w: 30, h: 30 },
        { value: '4:3', w: 34, h: 26 },
        { value: '3:4', w: 26, h: 34 },
        { value: '16:9', w: 38, h: 22 },
        // Whatever the gallery is: no shape of our own to draw.
        { value: 'auto', w: 32, h: 28, dashed: true },
        // The shape of the gallery's own pictures, measured on the page.
        { value: 'match', w: 30, h: 24, dashed: true },
    ];

    return (
        <div className="flex flex-col gap-5 rounded-xl border bg-background p-5" data-testid="widget-builder">
            {/* Say where it goes; the fields below are filled from it. */}
            <PlacementAssistant settings={s} onChange={onChange} t={t} />
            <SettingsField label={k('LABEL')}>
                <Input
                    value={s.label || ''}
                    onChange={(e) => onChange({ label: e.target.value })}
                    maxLength={60}
                />
            </SettingsField>

            <SettingsField label={k('RATIO')}>
                <div className="flex flex-wrap gap-1.5" data-testid="widget-ratios">
                    {RATIOS.map(({ value, w, h, dashed }) => {
                        const selected = s.aspectRatio === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                aria-pressed={selected}
                                data-testid={`widget-ratio-${value}`}
                                onClick={() => onChange({ aspectRatio: value })}
                                className={cn(
                                    'flex flex-1 basis-16 cursor-pointer flex-col items-center gap-1.5 rounded-lg border px-2 py-2 transition-colors',
                                    selected ? 'border-foreground/25 bg-muted/60' : 'border-input hover:bg-muted/40',
                                )}
                            >
                                <span className="flex h-9 items-center">
                                    <span
                                        className={cn(
                                            'block rounded-[3px] border',
                                            dashed && 'border-dashed',
                                            selected ? 'border-foreground/50 bg-muted' : 'border-input',
                                        )}
                                        style={{ width: w, height: h }}
                                    />
                                </span>
                                <span className="text-[11px] leading-none text-foreground">
                                    {value === 'auto' ? k('RATIO_AUTO') : value === 'match' ? k('RATIO_MATCH') : value}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </SettingsField>

            <SettingsField label={k('LOAD')}>
                <TileChoice
                    value={s.loadStrategy}
                    onSelect={(value) => onChange({ loadStrategy: value })}
                    options={[
                        { key: 'eager', Icon: Zap, label: k('LOAD_EAGER') },
                        { key: 'visible', Icon: Eye, label: k('LOAD_VISIBLE') },
                        { key: 'click', Icon: MousePointerClick, label: k('LOAD_CLICK') },
                    ]}
                />
            </SettingsField>

            {/* Only a carousel hides its children, so only a carousel has
                anything to reveal. Offering this anywhere else would be a
                setting with no effect. */}
            {s.containerBehavior === 'carousel' && (
                <SettingsField label={k('REVEAL')} hint={k('REVEAL_HINT')}>
                    <TileChoice
                        value={s.activateOnLoad}
                        onSelect={(value) => onChange({ activateOnLoad: value })}
                        options={[
                            { key: 'when_room_known', Icon: Sofa, label: k('REVEAL_WHEN_ROOM_KNOWN') },
                            { key: 'always', Icon: Eye, label: k('REVEAL_ALWAYS') },
                            { key: 'never', Icon: EyeOff, label: k('REVEAL_NEVER') },
                        ]}
                    />
                </SettingsField>
            )}

            <Collapsible data-testid="inline-advanced">
                <CollapsibleTrigger className="group/opts flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/50">
                    {k('ADVANCED')}
                    <ChevronDown className="size-4 transition-transform group-data-[state=open]/opts:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col gap-4 pt-4">
                    <p className="text-xs text-muted-foreground">{k('ADVANCED_HINT')}</p>

                    <SettingsField label={k('CONTAINER')} hint={k('CONTAINER_HINT')}>
                        <Input
                            value={s.containerSelector}
                            onChange={(e) => onChange({ containerSelector: e.target.value })}
                            placeholder=".product-gallery"
                            className="font-mono text-xs"
                        />
                    </SettingsField>

                    <SettingsField label={k('BEHAVIOR')} hint={k('BEHAVIOR_HINT')}>
                        <TileChoice
                            value={s.containerBehavior}
                            onSelect={(value) => onChange({ containerBehavior: value })}
                            options={[
                                { key: 'carousel', Icon: Layers, label: k('BEHAVIOR_CAROUSEL') },
                                { key: 'always_visible', Icon: Rows3, label: k('BEHAVIOR_ALWAYS_VISIBLE') },
                            ]}
                        />
                    </SettingsField>

                    {s.containerBehavior === 'carousel' && (
                        <SettingsField label={k('THUMB')} hint={k('THUMB_HINT')}>
                            <Input
                                value={s.thumbnailSelector || ''}
                                onChange={(e) => onChange({ thumbnailSelector: e.target.value })}
                                placeholder=".product-thumbnails"
                                className="font-mono text-xs"
                            />
                        </SettingsField>
                    )}

                    {/* Pointing at an element answers before/after/inside. A grid
                        or a carousel needs one more answer — which item — and a
                        vendor can say that in a number where a click cannot. */}
                    <SettingsField label={k('POSITION')} hint={k('POSITION_HINT')}>
                        <TileChoice
                            value={s.insertPosition}
                            onSelect={(value) => onChange({ insertPosition: value })}
                            options={[
                                { key: 'before', Icon: ArrowUpToLine, label: k('POSITION_BEFORE') },
                                { key: 'after', Icon: ArrowDownToLine, label: k('POSITION_AFTER') },
                                { key: 'prepend', Icon: ListStart, label: k('POSITION_PREPEND') },
                                { key: 'append', Icon: ListEnd, label: k('POSITION_APPEND') },
                                { key: 'index', Icon: Hash, label: k('POSITION_INDEX') },
                            ]}
                        />
                    </SettingsField>

                    {s.insertPosition === 'index' && (
                        <SettingsField label={k('POSITION_INDEX_LABEL')} hint={k('POSITION_INDEX_HINT')}>
                            <Input
                                type="number"
                                min={1}
                                value={s.insertIndex ?? 1}
                                onChange={(e) => onChange({ insertIndex: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                                className="w-24"
                                data-testid="inline-insert-index"
                            />
                        </SettingsField>
                    )}

                    <SettingsField label={k('MIN_HEIGHT')} hint={k('MIN_HEIGHT_HINT')}>
                        <Input
                            type="number"
                            min={0}
                            value={s.minHeight || ''}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                onChange({ minHeight: value > 0 ? value : undefined });
                            }}
                            placeholder="280"
                            className="w-24"
                            data-testid="inline-min-height"
                        />
                    </SettingsField>

                    <SettingsField label={k('CSS')} hint={k('CSS_HINT')}>
                        <Textarea
                            value={s.panelCSS || ''}
                            onChange={(e) => onChange({ panelCSS: e.target.value })}
                            placeholder={'margin: 12px 0;\ngrid-column: 1 / -1;'}
                            rows={4}
                            spellCheck={false}
                            className="font-mono text-xs"
                            data-testid="inline-panel-css"
                        />
                    </SettingsField>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};

/**
 * The panel settings a pick on the vendor's own page settles.
 *
 * Pointing at the gallery answers four things at once: where the panel goes,
 * whether the gallery parks its slides, where its thumbnail strip is, and what
 * shape its pictures are. Three of those are facts about the merchant's markup
 * that only a developer could have supplied, and they were four fields in a
 * form — which is why the panel was the hard half of the integration while the
 * button, placed by pointing, was the easy one.
 *
 * A reading can still be wrong on an unusual gallery, so everything it writes
 * stays editable underneath. What changes is where a vendor starts.
 */
export const inlineSettingsFromPick = (
    selector: string,
    position: string | null,
    reading: any,
): Record<string, any> => {
    const read = reading || {};
    return {
        containerSelector: selector,
        /*
         * Whether the page was actually read, or only pointed at.
         *
         * A pick from an older SDK on the vendor's own site carries no reading,
         * and the settings then keep their defaults — a carousel, no strip, 1:1.
         * Those are guesses, and describing them back to the vendor as "your
         * gallery is…" is the panel telling them about a page it never looked at.
         */
        readFromPage: !!(reading && reading.behavior),
        ...(read.behavior ? { containerBehavior: read.behavior } : {}),
        // An empty strip is an answer — "there isn't one" — not a missing value,
        // so it overwrites rather than leaving a stale selector behind.
        ...(typeof read.thumbnailSelector === 'string' ? { thumbnailSelector: read.thumbnailSelector } : {}),
        ...(read.aspectRatio ? { aspectRatio: read.aspectRatio } : {}),
        ...(position ? { insertPosition: position } : {}),
    };
};

/**
 * Settings for the in-gallery panel, shown when the visualizer's open mode is
 * `inline_embed`.
 *
 * Only the gallery container has no sensible default — there is no guessing a
 * merchant's markup — so everything else ships pre-filled and a vendor can switch
 * the mode on, paste one selector, and be done.
 */
export const InlineEmbedSettingsPanel = ({ settings, onChange, spaces = [], t }) => {
    const s = { ...defaultInlineEmbedSettings(), ...(settings || {}) };
    const k = (suffix: string) => t(`INTEGRATION.FEATURES.VISUALIZER.INLINE.${suffix}`);

    const roomHasWalls = (space: any) => roomSurfaceList(space).includes('walls');
    const roomHasFloor = (space: any) => roomSurfaceList(space).includes('floor');

    /**
     * The room nominated for each surface, resolved against the vendor's library.
     *
     * Two rooms rather than one because they are not interchangeable: a room the
     * analysis found no walls in cannot show a wall product at all — it renders
     * the untouched photo and reports nothing. `defaultSpaceId` is the single
     * room configs written before the split carry, and stands in for both.
     */
    const floorRoom = spaces.find((sp: any) => sp.id === (s.defaultSpaceIdFloor || s.defaultSpaceId));
    const wallsRoom = spaces.find((sp: any) => sp.id === (s.defaultSpaceIdWalls || s.defaultSpaceId));

    /** Grouped choices as icon tiles, the house pattern for a small closed set. */
    const TileChoice = ({ value, options, onSelect }) => (
        <div className="flex flex-wrap gap-1.5">
            {options.map(({ key, Icon, label }) => {
                const selected = value === key;
                return (
                    <button
                        key={key}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onSelect(key)}
                        className={cn(
                            'flex flex-1 basis-24 cursor-pointer flex-col items-center gap-1 rounded-lg border px-2 py-2 transition-colors',
                            selected
                                ? 'border-foreground/70 bg-muted/60 ring-1 ring-foreground/70'
                                : 'border-input hover:bg-muted/50',
                        )}
                    >
                        <Icon className="size-4 text-muted-foreground" />
                        <span className="text-[11px] leading-tight text-foreground">{label}</span>
                    </button>
                );
            })}
        </div>
    );

    /**
     * What the pick read off the gallery, in a sentence.
     *
     * Three facts about the merchant's own markup — does it park its slides, is
     * there a thumbnail strip, what shape are the pictures — that used to be
     * three form fields. A vendor cannot answer them and should not be asked
     * to; what they can do is read one sentence back and say whether it matches
     * the page they are looking at.
     */
    const reading = [
        s.containerBehavior === 'carousel' ? k('READ_CAROUSEL') : k('READ_STACKED'),
        s.thumbnailSelector ? k('READ_WITH_THUMBS') : k('READ_NO_THUMBS'),
        s.aspectRatio === 'auto'
            ? k('READ_RATIO_AUTO')
            : k('READ_RATIO').replace('{{ratio}}', s.aspectRatio),
    ].join(' ');

    /**
     * Only describe the gallery when it was genuinely read.
     *
     * An older SDK on the vendor's site can report which element was clicked
     * but nothing about it, so the settings fall back to defaults. Presenting
     * those as a reading is worse than silence: it is confidently wrong about
     * the page in front of them.
     */
    const wasRead = s.readFromPage === true;

    return (
        <div className="flex flex-col gap-8">
            {/*
                What the canvas found, and nothing more.
                
                This was a section of its own — a heading, an instruction to
                click the gallery, and a drawing of one. All three said what the
                canvas beside it was already showing and letting the vendor do.
                What is left is the one thing the canvas cannot say back: which
                element was taken, and what we could tell about it.
            */}
            {s.containerSelector ? (
                <div className="rounded-lg border bg-muted/40 p-3" data-testid="inline-gallery-reading">
                    <p className="text-sm text-foreground">
                        {wasRead ? reading : k('READ_UNAVAILABLE')}
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">{s.containerSelector}</p>
                </div>
            ) : (
                <p className="text-xs text-muted-foreground" data-testid="inline-gallery-unset">
                    {k('PLACEMENT_EMPTY')}
                </p>
            )}

            {/* One room per surface. A shopper opening a wall tile and a shopper
                opening a floor tile are not looking at the same room, and a room
                analysed without walls shows the first of those as an untouched
                photo — so the choice is made twice, each time against the surfaces
                the room actually has. */}
            <SettingsGroup title={k('DEFAULT_SPACE')} description={k('DEFAULT_SPACE_HINT')}>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <RoomPicker
                            testId="inline-default-room-floor"
                            label={k('DEFAULT_SPACE_FLOOR')}
                            value={s.defaultSpaceIdFloor || s.defaultSpaceId || ''}
                            onChange={(value) => onChange({ defaultSpaceIdFloor: value })}
                            spaces={spaces}
                            requiredSurface="floor"
                            k={k}
                        />
                        {floorRoom && !roomHasFloor(floorRoom) && (
                            <p className="mt-1 text-xs text-warning">{k('DEFAULT_SPACE_NO_FLOOR')}</p>
                        )}
                    </div>
                    <div>
                        <RoomPicker
                            testId="inline-default-room-walls"
                            label={k('DEFAULT_SPACE_WALLS')}
                            value={s.defaultSpaceIdWalls || s.defaultSpaceId || ''}
                            onChange={(value) => onChange({ defaultSpaceIdWalls: value })}
                            spaces={spaces}
                            requiredSurface="walls"
                            k={k}
                        />
                        {wallsRoom && !roomHasWalls(wallsRoom) && (
                            <p className="mt-1 text-xs text-warning">{k('DEFAULT_SPACE_NO_WALLS')}</p>
                        )}
                    </div>
                </div>
            </SettingsGroup>

        </div>
    );
};

// Code block with a copy affordance. A muted surface in both themes: an inverted
// black slab in light mode read as a different app.
const codeBlockClass = 'relative overflow-x-auto rounded-md border bg-muted/60 p-3 pe-10 font-mono text-xs leading-relaxed text-foreground';
const codeCopyButtonClass = 'absolute end-1.5 top-1.5 rounded p-1 text-muted-foreground transition-colors hover:bg-background hover:text-foreground';

// ==================== SETUP TAB ====================
/**
 * Connecting the website: which domains, whether each is live, and the snippet.
 *
 * General, not the visualizer's — every feature runs from this one snippet — so
 * it is its own page on Home. The visualizer's own switch and its standalone
 * trigger live on the Visualizer's integration page.
 */
const SetupTab = ({ integration, setIntegration, vendorAlias, t }) => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const [domainInput, setDomainInput] = useState('');
    const [domainError, setDomainError] = useState('');
    const [domainChecking, setDomainChecking] = useState(false);
    const [copied, setCopied] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

    const addDomain = async () => {
        let domain = domainInput.trim();
        try {
            // Extract hostname from full URLs (e.g., https://www.reddit.com/r/foo → reddit.com)
            // Preserve port for local dev (e.g., localhost:5500)
            const url = new URL(domain.startsWith('http') ? domain : 'https://' + domain);
            const hostname = url.hostname.replace(/^www\./, '');
            const port = url.port;
            domain = port && !['80', '443'].includes(port) ? `${hostname}:${port}` : hostname;
        } catch {
            // Not a valid URL, just clean up manually
            domain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '').replace(/[:?#].*$/, '');
        }
        if (!domain) return;
        if (integration.allowedDomains.includes(domain)) {
            setDomainError(t('INTEGRATION.SETUP.DOMAIN_ALREADY_ADDED'));
            return;
        }
        setDomainError('');
        setDomainChecking(true);
        try {
            const result = await sendAuthGuardedRequest(
                ApiMethod.GET,
                routes.integration.checkDomain(domain)
            ) as { available: boolean; claimedBy?: string };
            if (!result?.available) {
                setDomainError(t('INTEGRATION.SETUP.DOMAIN_TAKEN', { vendor: result?.claimedBy || 'another vendor' }));
                return;
            }
            setIntegration({
                ...integration,
                allowedDomains: [...integration.allowedDomains, domain],
            });
            setDomainInput('');
        } catch (error) {
            // If check fails, allow adding anyway (don't block on network issues)
            setIntegration({
                ...integration,
                allowedDomains: [...integration.allowedDomains, domain],
            });
            setDomainInput('');
        } finally {
            setDomainChecking(false);
        }
    };

    const removeDomain = (domain: string) => {
        const normalized = domain.toLowerCase().replace(/^www\./, '');
        const { [normalized]: _, ...remainingStatus } = integration.domainStatus || {};
        setIntegration({
            ...integration,
            allowedDomains: integration.allowedDomains.filter(d => d !== domain),
            domainStatus: remainingStatus,
        });
    };

    const [refreshingDomain, setRefreshingDomain] = useState<string | null>(null);
    const settingsAPI = useSettingsApi();

    const handleRefreshDomain = async (domain: string) => {
        const normalized = domain.toLowerCase().replace(/^www\./, '');
        setRefreshingDomain(normalized);
        try {
            // First, what the SDK itself reported: a load from any page on the
            // domain is recorded server-side, and this page only read that once,
            // when it opened.
            const config = await settingsAPI.findConfig();
            const recorded = config?.integration?.domainStatus?.[normalized];
            if (recorded?.connectedAt) {
                setIntegration(prev => ({
                    ...prev,
                    domainStatus: { ...prev.domainStatus, [normalized]: recorded },
                }));
                return;
            }

            // Nothing recorded yet: look for the snippet on the home page. A miss
            // proves little (the snippet often lives on product pages only), so it
            // no longer clears a status — only a find sets one.
            const result = await sendAuthGuardedRequest(
                ApiMethod.GET,
                routes.integration.verifyScript(normalized)
            ) as { found: boolean; domain: string };
            if (result?.found) {
                const now = new Date().toISOString();
                setIntegration(prev => ({
                    ...prev,
                    domainStatus: {
                        ...prev.domainStatus,
                        [normalized]: {
                            lastSeenAt: now,
                            connectedAt: prev.domainStatus?.[normalized]?.connectedAt || now,
                        },
                    },
                }));
            }
        } catch (error) {
            console.error('Error verifying script:', error);
        } finally {
            setRefreshingDomain(null);
        }
    };

    // Vendors on v2 configs (features map) get the v2 SDK URL; legacy v1 configs
    // stay on the v1 URL until their backoffice save triggers migration. Dual-URL
    // rollout keeps already-embedded v1 snippets working for 90 days.
    const sdkFileName = integration.schemaVersion === 2 ? 'ardealy-sdk-v2.js' : 'ardealy-sdk.js';
    const embedSnippet = `<script src="https://ardealy.com/sdk/${sdkFileName}" data-vendor="${vendorAlias || 'your-alias'}" async></script>`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(embedSnippet);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const [platform, setPlatform] = useState('SHOPIFY');
    const connectedCount = integration.allowedDomains.filter(
        (d: string) => !!integration.domainStatus?.[d.toLowerCase().replace(/^www\./, '')]?.connectedAt,
    ).length;

    return (
        <div className="flex flex-col gap-4">
            <WebsiteStatusBar
                integration={integration}
                connected={connectedCount}
                total={integration.allowedDomains.length}
                t={t}
            />

            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                {/* Step 1 — where the snippet is allowed to run */}
                <StepCard step={1} title={t('INTEGRATION.WEBSITE.STEP_DOMAINS', 'Add your domains')} description={t('INTEGRATION.SETUP.DOMAINS_HELPER')}>
                    <div className="flex gap-2">
                        <Input
                            placeholder={t('INTEGRATION.SETUP.DOMAINS_PLACEHOLDER')}
                            value={domainInput}
                            onChange={(e) => { setDomainInput(e.target.value); setDomainError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                            aria-invalid={!!domainError}
                        />
                        <Button type="button" variant="outline" className="shrink-0" onClick={addDomain} disabled={domainChecking || !domainInput.trim()}>
                            {domainChecking ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                            {t('INTEGRATION.SETUP.ADD_DOMAIN', 'Add domain')}
                        </Button>
                    </div>
                    {domainError && <p className="mt-1.5 text-xs text-destructive">{domainError}</p>}

                    {integration.allowedDomains.length === 0 ? (
                        <p className="mt-3 rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                            {t('INTEGRATION.WEBSITE.NO_DOMAINS', 'No domain yet. Add the address of your shop, like myshop.com.')}
                        </p>
                    ) : (
                        <ul className="mt-3 divide-y rounded-md border">
                            {integration.allowedDomains.map((domain: string) => {
                                const normalized = domain.toLowerCase().replace(/^www\./, '');
                                const status = integration.domainStatus?.[normalized];
                                const isConnected = !!status?.connectedAt;
                                const isDeleting = confirmingDelete === domain;
                                return (
                                    <li key={domain} className={cn('flex items-center gap-3 px-3 py-2', isDeleting && 'bg-destructive/5')}>
                                        <span
                                            aria-hidden
                                            className={cn('size-2 shrink-0 rounded-full', isDeleting ? 'bg-destructive' : isConnected ? 'bg-success' : 'bg-warning')}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium text-foreground">{domain}</p>
                                            <p className={cn('truncate text-xs', isDeleting ? 'text-destructive' : 'text-muted-foreground')}>
                                                {isDeleting
                                                    ? t('INTEGRATION.SETUP.CONFIRM_REMOVE')
                                                    : isConnected
                                                        ? `${t('INTEGRATION.SETUP.CONNECTED')} · ${t('INTEGRATION.SETUP.LAST_SEEN')}: ${status?.lastSeenAt ? new Date(status.lastSeenAt).toLocaleString() : '-'}`
                                                        : t('INTEGRATION.SETUP.WAITING_CONNECTION')}
                                            </p>
                                        </div>
                                        {isDeleting ? (
                                            <div className="flex shrink-0 gap-1.5">
                                                <Button
                                                    type="button"
                                                    size="xs"
                                                    variant="outline"
                                                    className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                                    onClick={() => { removeDomain(domain); setConfirmingDelete(null); }}
                                                >
                                                    {t('INTEGRATION.SETUP.REMOVE')}
                                                </Button>
                                                <Button type="button" size="xs" variant="ghost" className="text-muted-foreground" onClick={() => setConfirmingDelete(null)}>
                                                    {t('INTEGRATION.SETUP.CANCEL')}
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex shrink-0 gap-0.5">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            size="icon-sm"
                                                            variant="ghost"
                                                            className="text-muted-foreground hover:text-foreground"
                                                            aria-label={t('INTEGRATION.SETUP.REFRESH_STATUS') as string}
                                                            onClick={() => handleRefreshDomain(domain)}
                                                            disabled={refreshingDomain === normalized}
                                                        >
                                                            <RefreshCw className={cn('size-4', refreshingDomain === normalized && 'animate-spin')} />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">{t('INTEGRATION.SETUP.REFRESH_STATUS')}</TooltipContent>
                                                </Tooltip>
                                                <Button
                                                    type="button"
                                                    size="icon-sm"
                                                    variant="ghost"
                                                    className="text-muted-foreground hover:text-destructive"
                                                    aria-label={t('INTEGRATION.SETUP.REMOVE') as string}
                                                    onClick={() => setConfirmingDelete(domain)}
                                                >
                                                    <X className="size-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </StepCard>

                {/* Step 2 — the one snippet every feature runs from */}
                <StepCard step={2} title={t('INTEGRATION.WEBSITE.STEP_INSTALL', 'Install the snippet')} description={t('INTEGRATION.SETUP.EMBED_DESCRIPTION')}>
                    <div className={codeBlockClass}>
                        <code className="break-all">{embedSnippet}</code>
                        <button
                            type="button"
                            onClick={copyToClipboard}
                            className={codeCopyButtonClass}
                            aria-label={(copied ? t('INTEGRATION.WEBSITE.COPIED', 'Copied') : t('INTEGRATION.WEBSITE.COPY', 'Copy')) as string}
                        >
                            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                        </button>
                    </div>

                    {/* Where to paste it — one platform at a time instead of four accordions */}
                    <p className="mb-2 mt-4 text-xs font-medium text-muted-foreground">{t('INTEGRATION.SETUP.PLATFORM_HINTS')}</p>
                    <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        value={platform}
                        onValueChange={(v) => v && setPlatform(v)}
                        className="w-full"
                    >
                        {['SHOPIFY', 'WORDPRESS', 'DRUPAL', 'CUSTOM'].map((p) => (
                            <ToggleGroupItem key={p} value={p} className="flex-1">
                                {p.charAt(0) + p.slice(1).toLowerCase()}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        {t(`INTEGRATION.SETUP.${platform}_HINT`)}
                    </p>
                </StepCard>
            </div>
        </div>
    );
};

/** A numbered step: the order matters (a domain first, then the snippet), so it shows. */
const StepCard = ({ step, title, description, children }: { step: number; title: string; description?: string; children: React.ReactNode }) => (
    <Card className="gap-0 py-0">
        <CardContent className="p-5">
            <div className="mb-4 flex items-start gap-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium text-foreground">
                    {step}
                </span>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{title}</p>
                    {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
                </div>
            </div>
            {children}
        </CardContent>
    </Card>
);

// ==================== FEATURES TAB ====================
/**
 * Opening the visualizer from any button on the site, rather than only from the
 * button the SDK injects on product pages. The visualizer's, so it lives on the
 * Visualizer's integration page.
 */
const StandaloneTriggerCard = ({ t }) => {
    const [copiedStandalone, setCopiedStandalone] = useState(false);
    return (
        <SettingsGroup
            title={t('INTEGRATION.SETUP.STANDALONE_TITLE')}
            description={t('INTEGRATION.SETUP.STANDALONE_DESC')}
        >
            <div className={codeBlockClass}>
                <code className="text-muted-foreground">{'<!-- Add to any button, banner, or menu link -->'}</code><br />
                <code>{'<button onclick="ARdealy.openVisualizer()">'}</code><br />
                <code>{'  Try our room visualizer'}</code><br />
                <code>{'</button>'}</code>
                <button
                    type="button"
                    onClick={() => {
                        navigator.clipboard.writeText('<button onclick="ARdealy.openVisualizer()">Try our room visualizer</button>');
                        setCopiedStandalone(true);
                        setTimeout(() => setCopiedStandalone(false), 2000);
                    }}
                    className={codeCopyButtonClass}
                    aria-label={(copiedStandalone ? t('INTEGRATION.WEBSITE.COPIED', 'Copied') : t('INTEGRATION.WEBSITE.COPY', 'Copy')) as string}
                >
                    {copiedStandalone ? <Check className="size-4" /> : <Copy className="size-4" />}
                </button>
            </div>
            <p className="-mt-2 text-xs text-muted-foreground">
                {t('INTEGRATION.SETUP.STANDALONE_WITH_SKU')} <code className="rounded bg-muted px-1 font-mono text-xs text-foreground">{'ARdealy.openVisualizer("SKU-123")'}</code>
            </p>
        </SettingsGroup>
    );
};

/**
 * One line that answers "is my site connected, and what runs on it?".
 *
 * The connection is what this page sets up; the features are set up in their
 * own workspaces, so each is a link there. A vendor who connected and saw
 * nothing change can tell which switch is still off.
 */
const WebsiteStatusBar = ({ integration, connected, total, t }) => {
    const navigate = useNavigate();
    const isConnected = connected > 0;
    const features = [
        {
            key: 'visualizer',
            label: t('INTEGRATION.WEBSITE.VISUALIZER', 'Visualizer'),
            on: integration.features ? !!integration.features.visualizer?.enabled : isIntegrationEnabled(integration),
            route: '/visualizer/settings?tab=features',
        },
        {
            key: 'assistant',
            label: t('INTEGRATION.WEBSITE.ASSISTANT', 'Shopping Assistant'),
            on: !!integration.features?.chat?.enabled,
            route: '/assistant/settings?tab=general',
        },
    ];
    return (
        <Card className="gap-0 py-0">
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3.5">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <span
                        aria-hidden
                        className={cn(
                            'flex size-8 shrink-0 items-center justify-center rounded-full border',
                            isConnected ? 'border-success/30 bg-success/10 text-success' : 'bg-muted text-muted-foreground',
                        )}
                    >
                        {isConnected ? <Check className="size-4" /> : <Link2 className="size-4" />}
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                            {isConnected
                                ? t('INTEGRATION.WEBSITE.STATUS_CONNECTED', 'Your website is connected')
                                : t('INTEGRATION.WEBSITE.STATUS_NOT_CONNECTED', 'Not connected yet')}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                            {total === 0
                                ? t('INTEGRATION.WEBSITE.STATUS_NO_DOMAINS', 'Add a domain, then install the snippet.')
                                : t('INTEGRATION.WEBSITE.STATUS_COUNT', '{{connected}} of {{total}} domains connected', { connected, total })}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">{t('INTEGRATION.WEBSITE.RUNNING', 'What runs on your site')}</span>
                    {features.map((f) => (
                        <Button key={f.key} type="button" variant="outline" size="sm" onClick={() => navigate(f.route)}>
                            <span aria-hidden className={cn('size-1.5 rounded-full', f.on ? 'bg-success' : 'bg-muted-foreground/40')} />
                            {f.label}
                            <span className="text-muted-foreground">
                                {f.on ? t('INTEGRATION.WEBSITE.ON', 'On') : t('INTEGRATION.WEBSITE.OFF', 'Off')}
                            </span>
                        </Button>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export const FeaturesTab = ({ integration, setIntegration, t }) => {
    const visualizerSettings = integration.features?.visualizer?.settings as VisualizerSettings | undefined;
    const inlineEnabled = visualizerSettings?.inline?.enabled === true;

    /** How many features are on, which is what makes the layout question real. */
    const enabledCount = Object.values(integration.features || {}).filter((f: any) => !!f?.enabled).length;

    /**
     * Which surfaces the visualizer uses. Configs written before the choice
     * existed are read the way they behaved: a panel switched on was a widget
     * shop, anything else a button shop.
     */
    const declaredSurface = visualizerSettings?.surface;
    const visualizerSurface: VisualizerSurfaceMode =
        declaredSurface === 'button' || declaredSurface === 'widget' || declaredSurface === 'both'
            ? declaredSurface
            : inlineEnabled ? 'widget' : 'button';

    /**
     * Writing the choice down.
     *
     * `inline.enabled` is written alongside `surface` on purpose: an SDK cached
     * on a shopper's device predates `surface` and reads only that flag, so
     * leaving it behind would mean the shop kept doing what it did yesterday
     * while the backoffice claimed otherwise. Such an SDK reads `both` as a
     * widget shop — the widget without the button — until it is refreshed.
     */
    const setVisualizerSurface = (value: VisualizerSurfaceMode) => {
        updateFeatureSettings('visualizer', { surface: value });
        updateInlineSettings({ enabled: value !== 'button' });
    };

    // Per-feature toggle handler — updates `features[key].enabled`.
    // Also re-derives the legacy `isEnabled` mirror, so the top-of-tab gate + v1
    // SDK shim reflect the same state. It tracks ANY enabled feature — the same
    // rule handleSave persists — because `isEnabled` is the backend's master gate
    // for /config; mirroring only `visualizer` left a vendor who enabled just AR
    // or just 3D with local state that disagreed with what the save wrote back.
    const toggleFeature = (key: FeatureKey, enabled: boolean) => {
        setIntegration(prev => {
            const nextFeatures = {
                ...(prev.features || {} as Record<FeatureKey, FeatureBlock>),
            } as Record<FeatureKey, FeatureBlock>;
            nextFeatures[key] = { ...nextFeatures[key], enabled };
            const stillEnabled = Object.values(nextFeatures).filter(f => !!f?.enabled).length;
            return {
                ...prev,
                features: nextFeatures,
                isEnabled: stillEnabled > 0,
                /*
                 * A menu of one is not a menu.
                 *
                 * The layout choice is hidden below a second feature, so a saved
                 * `menu` left behind by a vendor who has since switched features
                 * off would be unreachable — and not merely cosmetic: the SDK
                 * reads `layoutMode`, so their shop would go on showing a trigger
                 * that opens a list with a single item, with no control left in
                 * the backoffice to undo it. Writing it back keeps the saved
                 * config, the preview and the vendor's own site saying the same
                 * thing.
                 */
                ...(stillEnabled < 2 ? { layoutMode: 'buttons' as LayoutMode } : {}),
            };
        });
    };

    const setLayoutMode = (mode: LayoutMode) => {
        setIntegration(prev => ({ ...prev, layoutMode: mode }));
    };

    /** Shallow-merge into `features[key].settings` — used by per-feature settings editors. */
    const updateFeatureSettings = (key: FeatureKey, patch: Record<string, any>) => {
        setIntegration(prev => {
            const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
            const current = nextFeatures[key] || ({ enabled: false, button: prev.button } as FeatureBlock);
            nextFeatures[key] = {
                ...current,
                settings: { ...(current.settings as any), ...patch } as any,
            };
            return { ...prev, features: nextFeatures };
        });
    };

    /**
     * Patch the visualizer's nested `settings.inline` block. Seeds from
     * defaultInlineEmbedSettings() on first touch so a half-filled block never
     * reaches the SDK — the SDK has its own fallbacks, but a config the vendor can
     * read back in full is easier to reason about than one full of holes.
     */
    const updateInlineSettings = (patch: Record<string, any>) => {
        setIntegration(prev => {
            const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
            const current = nextFeatures.visualizer;
            if (!current) return prev;
            const settings = (current.settings || {}) as VisualizerSettings;
            nextFeatures.visualizer = {
                ...current,
                settings: {
                    ...settings,
                    inline: { ...defaultInlineEmbedSettings(), ...(settings.inline || {}), ...patch },
                } as VisualizerSettings,
            };
            return { ...prev, features: nextFeatures };
        });
    };

    // Each feature gets a distinct icon so the rows are scannable at a glance
    const featureIcon = { visualizer: <Sofa />, ar: <Box />, model3d: <Rotate3d /> } as Record<FeatureKey, React.ReactNode>;

    return (
        <div className="flex flex-col gap-8">
            {/* One switch per slot in `integration.features`. A feature's own
                settings sit under its switch and only while it is on — a disabled
                feature has nothing to set. */}
            <SettingsGroup title={t('INTEGRATION.FEATURES.TITLE')}>
                {/*
                    Three cards, ticked or not, and then the options for whatever
                    is ticked underneath them.

                    They were three stacked rows, each with its settings nested
                    inside — so what was on and what was merely configurable sat at
                    the same level, and a vendor scanning the page could not see at
                    a glance which features their shop actually had. A row of cards
                    answers that first question on its own, and the second question
                    stops competing with it.
                */}
                <div className="grid grid-cols-3 gap-2">
                    {(['visualizer', 'ar', 'model3d'] as FeatureKey[]).map((key) => {
                        const on = !!integration.features?.[key]?.enabled;
                        return (
                            <button
                                key={key}
                                id={`integration-feature-${key}`}
                                type="button"
                                role="checkbox"
                                aria-checked={on}
                                onClick={() => toggleFeature(key, !on)}
                                className={cn(
                                    'relative flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border p-2 text-center transition-colors',
                                    on ? 'border-foreground/25 bg-muted/60' : 'border-input hover:bg-muted/40',
                                )}
                            >
                                <span className={cn('flex [&_svg]:size-5', on ? 'text-foreground' : 'text-muted-foreground')}>
                                    {featureIcon[key]}
                                </span>
                                <span className={cn('text-xs leading-tight', on ? 'text-foreground' : 'text-muted-foreground')}>
                                    {t(`INTEGRATION.FEATURES.${key.toUpperCase()}.TITLE`)}
                                </span>
                                {on && (
                                    <span className="absolute end-1.5 top-1.5 flex size-4 items-center justify-center rounded-full bg-foreground">
                                        <Check className="size-2.5 text-background" />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* One section per feature that is on. Open when it appears —
                    ticking a feature is a request to set it up — and foldable
                    once it is, so three of them do not bury the page. */}
                {(['visualizer', 'ar', 'model3d'] as FeatureKey[])
                    .filter((key) => !!integration.features?.[key]?.enabled)
                    .map((key) => {
                        const feature = integration.features?.[key];
                        return (
                            <Collapsible key={key} defaultOpen data-testid={`feature-options-${key}`}>
                                <CollapsibleTrigger className="group/opts flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted/50">
                                    {t(`INTEGRATION.FEATURES.${key.toUpperCase()}.TITLE`)}
                                    <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]/opts:rotate-180" />
                                </CollapsibleTrigger>
                                <CollapsibleContent className="flex flex-col gap-4 px-3 pb-1 pt-4">
                                    <p className="text-xs text-muted-foreground">
                                        {t(`INTEGRATION.FEATURES.${key.toUpperCase()}.DESC`)}
                                    </p>
                                    {key === 'visualizer' && (
                                    <>
                                        <VisualizerSurfaces
                                            surface={visualizerSurface}
                                            onSurface={setVisualizerSurface}
                                            openMode={(feature?.settings as VisualizerSettings | undefined)?.openMode}
                                            onOpenMode={(value) => updateFeatureSettings('visualizer', { openMode: value })}
                                            t={t}
                                        />
                                    </>
                                    )}
                                    {key === 'ar' && (
                                    <>
                                        <SettingsField
                                            label={t('INTEGRATION.FEATURES.AR.DESKTOP_FALLBACK')}
                                            hint={t('INTEGRATION.FEATURES.AR.IOS_HINT')}
                                        >
                                            <ToggleGroup
                                                type="single"
                                                variant="outline"
                                                size="sm"
                                                className="w-full"
                                                value={(feature?.settings as ArSettings | undefined)?.desktopFallback || 'qr'}
                                                onValueChange={(value) => value && updateFeatureSettings('ar', { desktopFallback: value })}
                                            >
                                                {(['qr', 'disabled'] as const).map(val => (
                                                    <ToggleGroupItem key={val} value={val} className="flex-1">
                                                        {t(`INTEGRATION.FEATURES.AR.FALLBACK_${val.toUpperCase()}`)}
                                                    </ToggleGroupItem>
                                                ))}
                                            </ToggleGroup>
                                        </SettingsField>
                                        <SettingsField label={t('INTEGRATION.FEATURES.AR.PLACEMENT')}>
                                            <ToggleGroup
                                                type="single"
                                                variant="outline"
                                                size="sm"
                                                className="w-full"
                                                value={(feature?.settings as ArSettings | undefined)?.placement || 'floor'}
                                                onValueChange={(value) => value && updateFeatureSettings('ar', { placement: value })}
                                            >
                                                <ToggleGroupItem value="floor" className="flex-1">{t('INTEGRATION.FEATURES.AR.PLACEMENT_FLOOR')}</ToggleGroupItem>
                                                <ToggleGroupItem value="wall" className="flex-1">{t('INTEGRATION.FEATURES.AR.PLACEMENT_WALL')}</ToggleGroupItem>
                                            </ToggleGroup>
                                        </SettingsField>
                                    </>
                                    )}
                                    {key === 'model3d' && (
                                    <>
                                        <SettingsField label={t('INTEGRATION.FEATURES.MODEL3D.MODAL_SIZE')}>
                                            <Select
                                                value={((feature?.settings as Model3DSettings | undefined)?.modalSize as string) || 'lg'}
                                                onValueChange={(value) => updateFeatureSettings('model3d', { modalSize: value })}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="sm">{t('INTEGRATION.FEATURES.MODEL3D.SIZE_SM')}</SelectItem>
                                                    <SelectItem value="md">{t('INTEGRATION.FEATURES.MODEL3D.SIZE_MD')}</SelectItem>
                                                    <SelectItem value="lg">{t('INTEGRATION.FEATURES.MODEL3D.SIZE_LG')}</SelectItem>
                                                    <SelectItem value="full">{t('INTEGRATION.FEATURES.MODEL3D.SIZE_FULL')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </SettingsField>
                                        <div className="flex items-center justify-between gap-4">
                                            <Label htmlFor="integration-model3d-autorotate" className="text-sm font-normal text-foreground">
                                                {t('INTEGRATION.FEATURES.MODEL3D.AUTO_ROTATE')}
                                            </Label>
                                            <Switch
                                                id="integration-model3d-autorotate"
                                                checked={!!(feature?.settings as Model3DSettings | undefined)?.autoRotate}
                                                onCheckedChange={(checked) => updateFeatureSettings('model3d', { autoRotate: checked })}
                                            />
                                        </div>
                                        {/* The colour is the vendor's own. */}
                                        <SettingsColorField
                                            id="integration-model3d-bg"
                                            label={t('INTEGRATION.FEATURES.MODEL3D.BG_COLOR')}
                                            value={(feature?.settings as Model3DSettings | undefined)?.bgColor || '#FFFFFF'}
                                            onChange={(value) => updateFeatureSettings('model3d', { bgColor: value })}
                                        />
                                    </>
                                    )}
                                </CollapsibleContent>
                            </Collapsible>
                        );
                    })}
            </SettingsGroup>

            {/* What shoppers are told about their own room: its size, the boxes, the price (A1). */}
            <SettingsGroup title={t('PRODUCT_PRICING.SETTINGS.GROUP', 'For shoppers')}>
                <ShopperMeasurementsSetting />
            </SettingsGroup>

            {/*
                Per-feature buttons, or one trigger that opens a menu of them —
                a question that only exists once there are two of them to choose
                between. With a single feature on, both answers put the same one
                button on the page, so asking is a decision with no consequence,
                and "menu" is the worse of two identical outcomes.
            */}
            {enabledCount > 1 && (
            <SettingsGroup
                title={t('INTEGRATION.FEATURES.LAYOUT.TITLE')}
                description={t('INTEGRATION.FEATURES.LAYOUT.DESC')}
            >
                <SettingsChoiceList
                    name={t('INTEGRATION.FEATURES.LAYOUT.TITLE')}
                    value={(integration.layoutMode || 'buttons') as LayoutMode}
                    onChange={setLayoutMode}
                    options={(['buttons', 'menu'] as const).map((mode) => ({
                        value: mode,
                        icon: mode === 'buttons' ? <Rows3 /> : <ChevronDown />,
                        label: t(`INTEGRATION.FEATURES.LAYOUT.${mode.toUpperCase()}`),
                        description: t(`INTEGRATION.FEATURES.LAYOUT.${mode.toUpperCase()}_DESC`),
                    }))}
                />
            </SettingsGroup>
            )}
        </div>
    );
};

// ==================== PRODUCT MATCHING TAB ====================
const ProductMatchingTab = ({ integration, setIntegration, t }) => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const [testUrl, setTestUrl] = useState('');
    const [testLoading, setTestLoading] = useState(false);
    const [testResult, setTestResult] = useState<any>(null);

    const handleTestMatch = async () => {
        if (!testUrl.trim()) return;
        setTestLoading(true);
        setTestResult(null);
        try {
            const result = await sendAuthGuardedRequest(
                ApiMethod.POST,
                routes.integration.testMatch,
                // Send the strategy too, so the tester evaluates what is currently
                // selected here — including unsaved edits. Without it the backend
                // fell back to the persisted strategy and reported on a config the
                // vendor may have just changed.
                {
                    url: testUrl.trim(),
                    matchConfig: integration.matchConfig,
                    matchStrategy: integration.matchStrategy,
                }
            );
            setTestResult(result);
        } catch (error) {
            setTestResult({ error: 'Failed to test URL. Please check the URL and try again.' });
        } finally {
            setTestLoading(false);
        }
    };

    const strategies = [
        { value: 'jsonld', label: t('INTEGRATION.MATCHING.JSONLD'), desc: t('INTEGRATION.MATCHING.JSONLD_DESC') },
        { value: 'url_pattern', label: t('INTEGRATION.MATCHING.URL_PATTERN'), desc: t('INTEGRATION.MATCHING.URL_PATTERN_DESC') },
        { value: 'css_selector', label: t('INTEGRATION.MATCHING.CSS_SELECTOR'), desc: t('INTEGRATION.MATCHING.CSS_SELECTOR_DESC') },
        { value: 'meta_tag', label: t('INTEGRATION.MATCHING.META_TAG'), desc: t('INTEGRATION.MATCHING.META_TAG_DESC') },
        { value: 'data_attribute', label: t('INTEGRATION.MATCHING.DATA_ATTRIBUTE'), desc: t('INTEGRATION.MATCHING.DATA_ATTRIBUTE_DESC') },
    ];

    const updateMatchConfig = (key: string, value: string) => {
        setIntegration({
            ...integration,
            matchConfig: { ...integration.matchConfig, [key]: value },
        });
    };

    const [exampleUrl, setExampleUrl] = useState(integration.matchConfig._exampleUrl || '');
    const [skuInUrl, setSkuInUrl] = useState(integration.matchConfig._exampleSku || '');
    const [patternManuallyEdited, setPatternManuallyEdited] = useState(false);

    const generatePattern = (url: string, sku: string) => {
        if (!url || !sku) return;
        try {
            const parsed = new URL(url.startsWith('http') ? url : 'https://' + url);
            const path = parsed.pathname.replace(/\/+$/, '');
            if (path.includes(sku)) {
                const pattern = path.replace(sku, '{sku}');
                setPatternManuallyEdited(false);
                setIntegration(prev => ({
                    ...prev,
                    matchConfig: { ...prev.matchConfig, urlPattern: pattern, _exampleSku: sku, _exampleUrl: url },
                }));
            }
        } catch {
            // invalid URL, ignore
        }
    };

    const showSkuNotFound = !!exampleUrl && !!skuInUrl && !integration.matchConfig.urlPattern && !patternManuallyEdited;

    const strategyLabels = {
        jsonld: 'JSON-LD',
        meta_tag: t('INTEGRATION.MATCHING.META_TAG'),
        css_selector: t('INTEGRATION.MATCHING.CSS_SELECTOR'),
        data_attribute: t('INTEGRATION.MATCHING.DATA_ATTRIBUTE'),
        url_pattern: t('INTEGRATION.MATCHING.URL_PATTERN'),
    };

    return (
        <div className="flex flex-col gap-8">
            {/* How the product is found on the page, and what that way needs. */}
            <SettingsGroup
                title={t('INTEGRATION.MATCHING.STRATEGY_LABEL')}
                description={t('INTEGRATION.MATCHING.DESCRIPTION')}
            >
                <SettingsChoiceList
                    name={t('INTEGRATION.MATCHING.STRATEGY_LABEL')}
                    value={integration.matchStrategy}
                    onChange={(value) => setIntegration({ ...integration, matchStrategy: value as any })}
                    options={strategies.map((strategy) => ({
                        value: strategy.value,
                        label: strategy.label,
                        description: strategy.desc,
                    }))}
                />

                {/* Strategy-specific config */}
                {integration.matchStrategy === 'jsonld' && (
                    <SettingsField label={t('INTEGRATION.MATCHING.JSONLD_FIELD')}>
                        <Select
                            value={integration.matchConfig.jsonldField || 'sku'}
                            onValueChange={(value) => updateMatchConfig('jsonldField', value)}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="sku">sku</SelectItem>
                                <SelectItem value="mpn">mpn</SelectItem>
                                <SelectItem value="gtin">gtin</SelectItem>
                                <SelectItem value="productID">productID</SelectItem>
                            </SelectContent>
                        </Select>
                    </SettingsField>
                )}
                {integration.matchStrategy === 'url_pattern' && (
                    <>
                        <SettingsField label={t('INTEGRATION.MATCHING.URL_EXAMPLE_LABEL')}>
                            <Input
                                placeholder={t('INTEGRATION.MATCHING.URL_EXAMPLE_PLACEHOLDER')}
                                value={exampleUrl}
                                onChange={(e) => { setExampleUrl(e.target.value); if (!patternManuallyEdited) generatePattern(e.target.value, skuInUrl); }}
                            />
                        </SettingsField>
                        <SettingsField label={t('INTEGRATION.MATCHING.URL_SKU_LABEL')}>
                            <Input
                                placeholder={t('INTEGRATION.MATCHING.URL_SKU_PLACEHOLDER')}
                                value={skuInUrl}
                                onChange={(e) => { setSkuInUrl(e.target.value); if (!patternManuallyEdited) generatePattern(exampleUrl, e.target.value); }}
                                aria-invalid={showSkuNotFound}
                            />
                            {showSkuNotFound && (
                                <p className="mt-1.5 text-xs text-destructive">{t('INTEGRATION.MATCHING.URL_SKU_NOT_FOUND')}</p>
                            )}
                        </SettingsField>
                        <SettingsField
                            label={t('INTEGRATION.MATCHING.URL_PATTERN_GENERATED')}
                            hint={t('INTEGRATION.MATCHING.URL_PATTERN_HINT')}
                        >
                            <Input
                                className="font-mono"
                                value={integration.matchConfig.urlPattern || ''}
                                onChange={(e) => { setPatternManuallyEdited(true); updateMatchConfig('urlPattern', e.target.value); }}
                                placeholder="/*/{sku}-*.html"
                            />
                        </SettingsField>
                    </>
                )}
                {integration.matchStrategy === 'css_selector' && (
                    <SettingsField label={t('INTEGRATION.MATCHING.CSS_SELECTOR_LABEL')}>
                        <Input
                            placeholder={t('INTEGRATION.MATCHING.CSS_SELECTOR_PLACEHOLDER')}
                            value={integration.matchConfig.cssSelector || ''}
                            onChange={(e) => updateMatchConfig('cssSelector', e.target.value)}
                        />
                    </SettingsField>
                )}
                {integration.matchStrategy === 'meta_tag' && (
                    <SettingsField label={t('INTEGRATION.MATCHING.META_TAG_LABEL')}>
                        <Input
                            placeholder={t('INTEGRATION.MATCHING.META_TAG_PLACEHOLDER')}
                            value={integration.matchConfig.metaTagName || ''}
                            onChange={(e) => updateMatchConfig('metaTagName', e.target.value)}
                        />
                    </SettingsField>
                )}
                {integration.matchStrategy === 'data_attribute' && (
                    <SettingsField label={t('INTEGRATION.MATCHING.DATA_ATTRIBUTE_LABEL')}>
                        <Input
                            placeholder={t('INTEGRATION.MATCHING.DATA_ATTRIBUTE_PLACEHOLDER')}
                            value={integration.matchConfig.dataAttribute || ''}
                            onChange={(e) => updateMatchConfig('dataAttribute', e.target.value)}
                        />
                    </SettingsField>
                )}
            </SettingsGroup>

            {/* URL fallback — tried only when the strategy above finds nothing. */}
            <SettingsToggleRow
                id="integration-url-fallback"
                label={t('INTEGRATION.MATCHING.URL_FALLBACK')}
                description={t('INTEGRATION.MATCHING.URL_FALLBACK_DESC')}
                checked={!!integration.urlMatchingEnabled}
                onCheckedChange={(checked) => setIntegration({ ...integration, urlMatchingEnabled: checked })}
            >
                <SettingsField label={t('INTEGRATION.MATCHING.URL_MATCH_FIELD')}>
                    <Select
                        value={integration.urlMatchField}
                        onValueChange={(value) => setIntegration({ ...integration, urlMatchField: value as any })}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="sku">{t('INTEGRATION.MATCHING.URL_MATCH_FIELD_SKU')}</SelectItem>
                            <SelectItem value="name">{t('INTEGRATION.MATCHING.URL_MATCH_FIELD_NAME')}</SelectItem>
                            <SelectItem value="commerce.link">{t('INTEGRATION.MATCHING.URL_MATCH_FIELD_URL')}</SelectItem>
                        </SelectContent>
                    </Select>
                </SettingsField>
            </SettingsToggleRow>

            {/* Live Tester */}
            <SettingsGroup
                title={t('INTEGRATION.MATCHING.LIVE_TESTER')}
                description={t('INTEGRATION.MATCHING.LIVE_TESTER_DESC')}
            >
                <div className="flex gap-2">
                    <Input
                        placeholder={t('INTEGRATION.MATCHING.TEST_URL_PLACEHOLDER')}
                        value={testUrl}
                        onChange={(e) => setTestUrl(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleTestMatch(); }}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        className="h-9 whitespace-nowrap px-4"
                        disabled={testLoading || !testUrl.trim()}
                        onClick={handleTestMatch}
                    >
                        {testLoading ? <Loader2 className="size-4 animate-spin" /> : t('INTEGRATION.MATCHING.TEST_BUTTON')}
                    </Button>
                </div>

                {testResult && !testResult.error && (
                    <div>
                        <div className="mb-1.5"><SettingsLabel>{t('INTEGRATION.MATCHING.TEST_RESULTS')}</SettingsLabel></div>
                        <ul className="divide-y rounded-lg border">
                            {testResult.results?.map((r: any, i: number) => (
                                <li key={i} className="flex flex-col gap-1.5 px-3 py-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="min-w-0 truncate text-sm text-foreground">
                                            {strategyLabels[r.strategy] || r.strategy}
                                            {r.field ? ` (${r.field})` : ''}
                                        </span>
                                        {/* The vendor's own strategy. Everything else in this list is a
                                            suggestion, so without this the row that actually answers
                                            "does my setup work?" is indistinguishable from the rest. */}
                                        {r.isConfigured && (
                                            <Badge variant="secondary" className="shrink-0">
                                                {t('INTEGRATION.MATCHING.YOUR_CONFIG')}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={cn(
                                            'min-w-0 flex-1 truncate font-mono text-xs',
                                            r.detectedValue ? 'text-foreground' : 'text-muted-foreground',
                                        )}>
                                            {r.detectedValue || t('INTEGRATION.MATCHING.TEST_RESULT_NOT_FOUND')}
                                        </span>
                                        {r.detectedValue && (
                                            <Badge className={cn(
                                                'shrink-0 border-transparent',
                                                r.productMatch?.found ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive',
                                            )}>
                                                {r.productMatch?.found ? t('INTEGRATION.MATCHING.TEST_RESULT_FOUND') : t('INTEGRATION.MATCHING.TEST_RESULT_NOT_FOUND')}
                                            </Badge>
                                        )}
                                        {/* No "use this" on the configured row — it is already in use. */}
                                        {r.detectedValue && r.productMatch?.found && !r.isConfigured && (
                                            <Button
                                                type="button"
                                                size="xs"
                                                variant="outline"
                                                className="shrink-0"
                                                onClick={() => {
                                                    const update: any = { matchStrategy: r.strategy };
                                                    if (r.strategy === 'jsonld' && r.field) {
                                                        update.matchConfig = { ...integration.matchConfig, jsonldField: r.field };
                                                    } else if (r.strategy === 'meta_tag' && r.field) {
                                                        update.matchConfig = { ...integration.matchConfig, metaTagName: r.field };
                                                    } else if (r.strategy === 'css_selector' && r.field) {
                                                        update.matchConfig = { ...integration.matchConfig, cssSelector: r.field };
                                                    }
                                                    setIntegration({ ...integration, ...update });
                                                }}
                                            >
                                                {t('INTEGRATION.MATCHING.USE_THIS_METHOD')}
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {testResult?.error && (
                    <p className="text-xs text-destructive">{testResult.error}</p>
                )}
            </SettingsGroup>
        </div>
    );
};

// ==================== BUTTON BUILDER ====================
export const ButtonBuilder = ({ button, onButtonChange, semanticColors = null, t, openMode = undefined, onOpenModeChange = undefined }) => {
    const [editingLabel, setEditingLabel] = useState(false);
    const [labelDraft, setLabelDraft] = useState(button.label);
    const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
    const [colorField, setColorField] = useState<string>('');
    const [advancedAnchorEl, setAdvancedAnchorEl] = useState<HTMLElement | null>(null);
    const dragRef = useRef<{ field: string; startX: number; startY: number; startVal: number } | null>(null);
    const boundingRef = useRef<HTMLDivElement | null>(null);
    const [btnSize, setBtnSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    // Popovers stay anchored to whichever swatch/gear opened them
    const colorAnchorRef = useRef<HTMLElement | null>(null);
    colorAnchorRef.current = colorAnchorEl;
    const advancedAnchorRef = useRef<HTMLElement | null>(null);
    advancedAnchorRef.current = advancedAnchorEl;

    const btn = button;

    const DEFAULT_ICON_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42 13.98 8.42 12C8.42 10.02 10.02 8.42 12 8.42C13.98 8.42 15.58 10.02 15.58 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.4C18.82 5.8 15.53 3.72 12 3.72C8.47 3.72 5.18 5.8 2.89 9.4C1.99 10.81 1.99 13.18 2.89 14.59C5.18 18.19 8.47 20.27 12 20.27Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const iconSvg = btn.iconSvg || DEFAULT_ICON_SVG;
    const renderBuilderIcon = () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', color: 'inherit' }} dangerouslySetInnerHTML={{ __html: iconSvg }} />
    );

    // The live preview renders the vendor's stored button config — all inline by design
    const getBuilderPreviewStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            borderRadius: btn.borderRadius,
            padding: `${btn.paddingV || '12'}px ${btn.paddingH || '24'}px`,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: btn.iconOnly ? '0' : '8px',
            fontSize: '14px',
            fontWeight: 500,
            width: btn.fullWidth ? '100%' : 'auto',
            justifyContent: 'center',
            transition: dragRef.current ? 'none' : 'opacity 0.2s',
        };
        switch (btn.buttonStyle) {
            case 'outlined':
                return { ...base, backgroundColor: 'transparent', color: btn.borderColor || btn.backgroundColor, border: `2px solid ${btn.borderColor || btn.backgroundColor}` };
            case 'link':
                return { ...base, backgroundColor: 'transparent', color: btn.backgroundColor, border: 'none', padding: `${btn.paddingV || '4'}px ${btn.paddingH || '8'}px`, textDecoration: 'underline', textUnderlineOffset: '3px' };
            default:
                return { ...base, backgroundColor: btn.backgroundColor, color: btn.textColor, border: 'none' };
        }
    };

    const paddingH = parseInt(btn.paddingH) || 24;
    const paddingV = parseInt(btn.paddingV) || 12;
    const radius = parseInt(btn.borderRadius) || 8;

    const openColorPopover = (e: React.MouseEvent<HTMLElement>, field: string) => {
        setColorAnchorEl(e.currentTarget);
        setColorField(field);
    };
    const closeColorPopover = () => { setColorAnchorEl(null); setColorField(''); };

    const dimConfig: Record<string, { value: number; max: number; key: string; transform: (v: number) => string }> = {
        paddingH: { value: paddingH, max: 60, key: 'paddingH', transform: (v) => String(v) },
        paddingV: { value: paddingV, max: 40, key: 'paddingV', transform: (v) => String(v) },
        borderRadius: { value: radius, max: 50, key: 'borderRadius', transform: (v) => `${v}px` },
    };

    // Drag-to-adjust handlers
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!dragRef.current) return;
            const { field, startX, startY, startVal } = dragRef.current;
            const cfg = dimConfig[field];
            if (!cfg) return;
            // Use horizontal delta for paddingH/borderRadius, vertical (inverted) for paddingV
            const delta = field === 'paddingV'
                ? (startY - e.clientY)
                : (e.clientX - startX);
            const newVal = Math.max(0, Math.min(cfg.max, Math.round(startVal + delta / 2)));
            onButtonChange(cfg.key, cfg.transform(newVal));
        };
        const handleMouseUp = () => {
            if (dragRef.current) {
                dragRef.current = null;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    });

    // Track bounding box size so measurements match button dimensions
    useEffect(() => {
        const el = boundingRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const { width, height } = entries[0].contentRect;
            setBtnSize({ w: Math.round(width), h: Math.round(height) });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const startDrag = (e: React.MouseEvent, field: string) => {
        e.preventDefault();
        const cfg = dimConfig[field];
        if (!cfg) return;
        dragRef.current = { field, startX: e.clientX, startY: e.clientY, startVal: cfg.value };
        document.body.style.cursor = field === 'paddingV' ? 'ns-resize' : 'ew-resize';
        document.body.style.userSelect = 'none';
    };

    // Color field config based on buttonStyle
    const getColorSwatches = () => {
        switch (btn.buttonStyle) {
            case 'filled':
                return [
                    { field: 'backgroundColor', color: btn.backgroundColor, label: t('INTEGRATION.BUTTON.BG') },
                    { field: 'textColor', color: btn.textColor, label: t('INTEGRATION.BUTTON.TEXT') },
                ];
            case 'outlined':
                return [
                    { field: 'backgroundColor', color: btn.backgroundColor, label: t('INTEGRATION.BUTTON.ACCENT_COLOR') },
                    { field: 'borderColor', color: btn.borderColor || btn.backgroundColor, label: t('INTEGRATION.BUTTON.BORDER') },
                ];
            case 'link':
                return [
                    { field: 'backgroundColor', color: btn.backgroundColor, label: t('INTEGRATION.BUTTON.ACCENT_COLOR') },
                ];
            default:
                return [];
        }
    };

    const colorFieldValue = colorField === 'borderColor' ? (btn.borderColor || btn.backgroundColor) :
        colorField === 'textColor' ? btn.textColor :
        colorField === 'backgroundColor' ? btn.backgroundColor : '';

    const measureLineClass = 'shrink-0 bg-muted-foreground/50 transition-colors group-hover/measure:bg-muted-foreground/80';
    const measurePillClass = 'whitespace-nowrap border bg-background px-2 py-0.5 font-mono text-[11px] text-muted-foreground transition-colors group-hover/measure:border-foreground group-hover/measure:text-foreground';

    // Placed to the right — horizontal T-caps ──, vertical stems |, column layout
    // size = button height so the measurement spans the full button
    const VMeasure = ({ field, size }: { field: string; size: number }) => {
        const cfg = dimConfig[field];
        if (!cfg) return null;
        const stemSize = Math.max(4, (size - 24) / 2); // subtract pill height (~24px), split between top+bottom
        return (
            <div
                data-testid={`dim-${field}`}
                onMouseDown={(e) => startDrag(e, field)}
                className="group/measure flex cursor-ns-resize select-none flex-col items-center justify-center"
                style={{ height: size > 0 ? size : 'auto' }}
            >
                <span className={cn(measureLineClass, 'h-px w-3')} />
                <span className={cn(measureLineClass, 'w-px')} style={{ height: stemSize }} />
                <span className={measurePillClass}>{cfg.value}px</span>
                <span className={cn(measureLineClass, 'w-px')} style={{ height: stemSize }} />
                <span className={cn(measureLineClass, 'h-px w-3')} />
            </div>
        );
    };

    // Placed below — vertical T-caps |, horizontal stems ──, row layout
    // size = button width so the measurement spans the full button
    const HMeasure = ({ field, size }: { field: string; size: number }) => {
        const cfg = dimConfig[field];
        if (!cfg) return null;
        const stemSize = Math.max(4, (size - 40) / 2); // subtract pill width (~40px), split between left+right
        return (
            <div
                data-testid={`dim-${field}`}
                onMouseDown={(e) => startDrag(e, field)}
                className="group/measure flex cursor-ew-resize select-none items-center justify-center"
                style={{ width: size > 0 ? size : 'auto' }}
            >
                <span className={cn(measureLineClass, 'h-3 w-px')} />
                <span className={cn(measureLineClass, 'h-px')} style={{ width: stemSize }} />
                <span className={measurePillClass}>{cfg.value}px</span>
                <span className={cn(measureLineClass, 'h-px')} style={{ width: stemSize }} />
                <span className={cn(measureLineClass, 'h-3 w-px')} />
            </div>
        );
    };

    // Corner handle for border-radius — drag diagonally on the button corner
    const CornerHandle = () => (
        <span
            data-testid="corner-handle"
            onMouseDown={(e) => { e.stopPropagation(); startDrag(e, 'borderRadius'); }}
            className="absolute -left-3 -top-3 z-[2] flex size-6 cursor-nwse-resize items-center justify-center rounded-full border-[1.5px] bg-background transition-transform hover:scale-110 hover:border-foreground"
        >
            {/* Arc icon */}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                    d="M1 11 Q1 1 11 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    className="text-muted-foreground"
                />
            </svg>
        </span>
    );

    const startLabelEdit = () => {
        if (btn.iconOnly) return;
        setLabelDraft(btn.label);
        setEditingLabel(true);
    };
    const commitLabel = () => {
        setEditingLabel(false);
        onButtonChange('label', sanitizeButtonLabel(labelDraft));
    };

    // The floating panels inside the canvas share the same glass treatment
    const canvasPanelClass = 'border bg-background/85 backdrop-blur-md';

    return (
        <div className="flex flex-col gap-5">
            {/* Hint */}
            <p className="text-xs text-muted-foreground">
                {t('INTEGRATION.BUTTON.BUILDER_HINT')}
            </p>

            {/* Canvas */}
            <div
                className="relative flex min-h-60 items-center justify-center bg-muted p-4"
                style={{
                    backgroundImage: 'radial-gradient(color-mix(in srgb, var(--foreground) 20%, transparent) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            >
                {/* Style Switcher — top-start inside canvas */}
                <div className={cn(canvasPanelClass, 'absolute start-3 top-3 z-[3] flex gap-0.5 p-[3px]')}>
                    {(['filled', 'outlined', 'link'] as const).map((style) => (
                        <button
                            key={style}
                            type="button"
                            data-testid={`style-${style}`}
                            onClick={() => onButtonChange('buttonStyle', style)}
                            className={cn(
                                'flex cursor-pointer items-center justify-center px-2 py-1 transition-colors hover:bg-foreground/[0.04]',
                                btn.buttonStyle === style && 'bg-foreground/[0.08]',
                            )}
                        >
                            {/* The Aa sample previews the vendor's own colours */}
                            <span style={{
                                display: 'inline-block',
                                padding: '2px 6px',
                                fontSize: '10px',
                                fontWeight: 500,
                                borderRadius: '3px',
                                lineHeight: 1.2,
                                ...(style === 'filled' ? { backgroundColor: btn.backgroundColor, color: btn.textColor } :
                                    style === 'outlined' ? { color: btn.borderColor || btn.backgroundColor, border: `1.5px solid ${btn.borderColor || btn.backgroundColor}` } :
                                    { color: btn.backgroundColor, textDecoration: 'underline' }),
                            }}>Aa</span>
                        </button>
                    ))}
                </div>
                {/* Outer wrapper: button with measurements extending outward */}
                <div className={cn('flex items-start', btn.fullWidth ? 'w-full px-12' : 'w-auto px-0')}>
                    {/* Button + horizontal measurement below */}
                    <div className={cn('flex flex-col items-center', btn.fullWidth && 'flex-1')}>
                        {/* Live button with corner handle + edit badge */}
                        <div
                            ref={boundingRef}
                            onClick={!editingLabel ? startLabelEdit : undefined}
                            className={cn('relative', btn.iconOnly ? 'cursor-default' : 'cursor-text', btn.fullWidth && 'w-full')}
                        >
                                {btn.buttonStyle !== 'link' && <CornerHandle />}
                                <button type="button" style={getBuilderPreviewStyles()} data-testid="builder-preview-button">
                                    {(btn.iconEnabled || btn.iconOnly) && renderBuilderIcon()}
                                    {!btn.iconOnly && (
                                        editingLabel ? (
                                            <input
                                                autoFocus
                                                data-testid="label-input"
                                                value={labelDraft}
                                                onChange={(e) => setLabelDraft(e.target.value)}
                                                onBlur={commitLabel}
                                                onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    outline: 'none',
                                                    color: 'inherit',
                                                    font: 'inherit',
                                                    padding: 0,
                                                    width: Math.max(120, (labelDraft || '').length * 7 + 24),
                                                    textAlign: 'center',
                                                }}
                                            />
                                        ) : (
                                            <span dangerouslySetInnerHTML={{ __html: sanitizeButtonLabel(btn.label || t('INTEGRATION.BUTTON.LABEL_PLACEHOLDER')) }} />
                                        )
                                    )}
                                </button>
                                {!btn.iconOnly && !editingLabel && (
                                    <span className="absolute -right-2.5 -top-2.5 flex size-[22px] cursor-text items-center justify-center rounded-full border-[1.5px] bg-background transition-transform hover:scale-110 hover:border-foreground">
                                        <Pencil className="size-3 text-muted-foreground" />
                                    </span>
                                )}
                        </div>

                        {/* Horizontal padding — shown below, width = button width */}
                        <HMeasure field="paddingH" size={btnSize.w} />
                    </div>

                    {/* Vertical padding — shown to the right, height = button height */}
                    <VMeasure field="paddingV" size={btnSize.h} />
                </div>

                {/* Color Swatches — bottom-end inside canvas */}
                <div className={cn(canvasPanelClass, 'absolute bottom-3 end-3 z-[3] flex gap-3 px-2.5 py-1.5')}>
                    {getColorSwatches().map((swatch) => (
                        <button
                            key={swatch.field}
                            type="button"
                            data-testid={`swatch-${swatch.field}`}
                            onClick={(e) => openColorPopover(e, swatch.field)}
                            className="flex cursor-pointer flex-col items-center gap-1"
                        >
                            <span
                                className="size-[22px] rounded-full border-2 transition-transform hover:scale-110"
                                style={{ backgroundColor: swatch.color }}
                            />
                            <span className="text-[9px] text-muted-foreground">
                                {swatch.label}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Toggle chips — bottom-start inside canvas */}
                <div className={cn(canvasPanelClass, 'absolute bottom-3 start-3 z-[3] flex gap-0.5 p-[3px]')}>
                    {/* Full Width */}
                    <button
                        type="button"
                        onClick={() => onButtonChange('fullWidth', !btn.fullWidth)}
                        className={cn(
                            'flex cursor-pointer items-center gap-1 px-2 py-1 transition-colors hover:bg-foreground/[0.04]',
                            btn.fullWidth && 'bg-foreground/[0.08]',
                        )}
                    >
                        <MoveHorizontal className={cn('size-3.5', btn.fullWidth ? 'text-foreground' : 'text-muted-foreground')} />
                        <span className={cn('whitespace-nowrap text-[10px] font-medium', btn.fullWidth ? 'text-foreground' : 'text-muted-foreground')}>
                            {t('INTEGRATION.BUTTON.FULL_WIDTH')}
                        </span>
                    </button>
                    {/* Show Icon */}
                    <button
                        type="button"
                        onClick={() => { const v = !btn.iconEnabled; onButtonChange('iconEnabled', v); if (!v) onButtonChange('iconOnly', false); }}
                        className={cn(
                            'flex cursor-pointer items-center gap-1 px-2 py-1 transition-colors hover:bg-foreground/[0.04]',
                            btn.iconEnabled && 'bg-foreground/[0.08]',
                        )}
                    >
                        {btn.iconEnabled
                            ? <Eye className="size-3.5 text-foreground" />
                            : <EyeOff className="size-3.5 text-muted-foreground" />
                        }
                        <span className={cn('whitespace-nowrap text-[10px] font-medium', btn.iconEnabled ? 'text-foreground' : 'text-muted-foreground')}>
                            {t('INTEGRATION.BUTTON.SHOW_ICON')}
                        </span>
                    </button>
                    {/* Icon Only */}
                    {btn.iconEnabled && (
                        <button
                            type="button"
                            onClick={() => onButtonChange('iconOnly', !btn.iconOnly)}
                            className={cn(
                                'flex cursor-pointer items-center gap-1 px-2 py-1 transition-colors hover:bg-foreground/[0.04]',
                                btn.iconOnly && 'bg-foreground/[0.08]',
                            )}
                        >
                            <MousePointerClick className={cn('size-3.5', btn.iconOnly ? 'text-foreground' : 'text-muted-foreground')} />
                            <span className={cn('whitespace-nowrap text-[10px] font-medium', btn.iconOnly ? 'text-foreground' : 'text-muted-foreground')}>
                                {t('INTEGRATION.BUTTON.ICON_ONLY')}
                            </span>
                        </button>
                    )}
                </div>

                {/* Advanced gear — top-end inside canvas */}
                <button
                    type="button"
                    onClick={(e) => setAdvancedAnchorEl(e.currentTarget)}
                    className={cn(canvasPanelClass, 'absolute end-3 top-3 z-[3] flex size-[30px] cursor-pointer items-center justify-center transition-colors hover:border-input')}
                >
                    <Settings className="size-4 text-muted-foreground" />
                </button>
            </div>

            {/* Color Popover */}
            <Popover open={Boolean(colorAnchorEl)} onOpenChange={(open) => { if (!open) closeColorPopover(); }}>
                <PopoverAnchor virtualRef={colorAnchorRef as any} />
                <PopoverContent side="bottom" align="center" className="w-auto p-4">
                    <div className="flex min-w-40 flex-col gap-3">
                        <input
                            type="color"
                            value={colorFieldValue}
                            onChange={(e) => onButtonChange(colorField, e.target.value)}
                            style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer', padding: 0 }}
                        />
                        <Input
                            value={colorFieldValue}
                            onChange={(e) => onButtonChange(colorField, e.target.value)}
                            className="font-mono text-xs"
                        />
                    </div>
                </PopoverContent>
            </Popover>

            {/* Advanced Popover */}
            <Popover open={Boolean(advancedAnchorEl)} onOpenChange={(open) => { if (!open) setAdvancedAnchorEl(null); }}>
                <PopoverAnchor virtualRef={advancedAnchorRef as any} />
                <PopoverContent side="bottom" align="end" className="w-auto min-w-80 max-w-[400px] p-5">
                    <div className="flex flex-col gap-4">
                        {/* Custom Icon SVG */}
                        {btn.iconEnabled && (
                            <div className="flex flex-col gap-2">
                                <p className="text-xs font-medium text-foreground">
                                    {t('INTEGRATION.BUTTON.CUSTOM_ICON')}
                                </p>
                                <Textarea
                                    rows={3}
                                    placeholder='<svg width="18" height="18" viewBox="0 0 24 24">...</svg>'
                                    value={btn.iconSvg}
                                    onChange={(e) => onButtonChange('iconSvg', e.target.value)}
                                    className="font-mono text-xs"
                                />
                                {btn.iconSvg && (
                                    <div className="flex items-center gap-3">
                                        <span className="inline-flex border p-1">
                                            {renderBuilderIcon()}
                                        </span>
                                        <Button
                                            type="button"
                                            size="xs"
                                            variant="ghost"
                                            className="text-muted-foreground"
                                            onClick={() => onButtonChange('iconSvg', '')}
                                        >
                                            {t('INTEGRATION.BUTTON.RESET_ICON')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Custom CSS */}
                        <div className="flex flex-col gap-2">
                            <p className="text-xs font-medium text-foreground">
                                {t('INTEGRATION.BUTTON.CUSTOM_CSS')}
                            </p>
                            <Textarea
                                rows={3}
                                placeholder={t('INTEGRATION.BUTTON.CUSTOM_CSS_PLACEHOLDER')}
                                value={btn.customCSS}
                                onChange={(e) => onButtonChange('customCSS', e.target.value)}
                                className="font-mono text-xs"
                            />
                        </div>
                        {/* Open Mode */}
                        {onOpenModeChange && (
                            <div className="flex flex-col gap-2">
                                <p className="text-xs font-medium text-foreground">
                                    {t('INTEGRATION.BUTTON.OPEN_MODE')}
                                </p>
                                <div className="flex gap-[3px] border bg-foreground/5 p-[3px]">
                                    {([
                                        { value: 'iframe_overlay', icon: <Maximize2 className="size-[15px]" />, label: t('INTEGRATION.BUTTON.IFRAME_OVERLAY') },
                                        { value: 'new_tab', icon: <ExternalLink className="size-[15px]" />, label: t('INTEGRATION.BUTTON.NEW_TAB') },
                                    ]).map(({ value, icon, label }) => {
                                        const active = (openMode || 'iframe_overlay') === value;
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => onOpenModeChange(value)}
                                                className={cn(
                                                    'flex flex-1 cursor-pointer select-none items-center justify-center gap-1.5 px-3 py-1.5 transition-colors',
                                                    active
                                                        ? 'border border-input bg-background text-foreground'
                                                        : 'border border-transparent text-muted-foreground hover:bg-foreground/5',
                                                )}
                                            >
                                                {icon}
                                                <span className={cn('whitespace-nowrap text-xs', active ? 'font-medium' : 'font-normal')}>
                                                    {label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

// ==================== DEVICE SWITCHER ====================
// Neutral segmented switcher: muted track, active segment rises with the page background.
export const DeviceSwitcher = ({ activeDevice, onDeviceChange, semanticColors = null, t }) => (
    <div className="inline-flex gap-[3px] border bg-muted p-[3px]">
        {([
            { key: 'web', icon: <Monitor className="size-4" />, label: t('INTEGRATION.DEVICE.WEB') },
            { key: 'mobile', icon: <Smartphone className="size-4" />, label: t('INTEGRATION.DEVICE.MOBILE') },
        ] as const).map(({ key, icon, label }) => {
            const isActive = activeDevice === key;
            return (
                <button
                    key={key}
                    type="button"
                    data-testid={`device-${key}`}
                    onClick={() => onDeviceChange(key)}
                    className={cn(
                        'flex cursor-pointer select-none items-center gap-1.5 px-4 py-1.5 transition-colors',
                        isActive
                            ? 'border border-input bg-background text-foreground'
                            : 'border border-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                >
                    {icon}
                    <span className={cn('text-sm', isActive ? 'font-medium' : 'font-normal')}>
                        {label}
                    </span>
                </button>
            );
        })}
    </div>
);

// Helper: merge web button with sparse mobile overrides
export const getResolvedMobileButton = (web: IntegrationButtonConfig, mobile?: Partial<IntegrationButtonConfig> | null): IntegrationButtonConfig => {
    if (!mobile) return { ...web };
    return { ...web, ...mobile };
};

// ==================== BUTTON DESIGN TAB ====================
export const ButtonDesignTab = ({ integration, setIntegration, t }) => {
    const spacesAPI = useSpacesApi();

    /**
     * The vendor's room library, for the inline panel's default-room picker.
     * Fetched only once that panel is actually on — every other visitor to this
     * tab would be paying for a list nothing renders.
     */
    const [spaces, setSpaces] = useState<any[]>([]);
    const inlineSettings = (integration.features?.visualizer?.settings as VisualizerSettings | undefined)?.inline;
    const inlineEnabled = inlineSettings?.enabled === true;

    useEffect(() => {
        if (!inlineEnabled || spaces.length) return;
        let cancelled = false;
        spacesAPI.findAllSpaces()
            .then(res => {
                if (cancelled) return;
                const all = res?.data ?? [];
                // Only the rooms the vendor has actually added to their library.
                // This endpoint also returns the public rooms they *could* add, and
                // the visualizer answers 401 for those — a shopper's panel could
                // never open one, so offering it here is offering a dead end.
                // `isAssigned` is absent on older API builds; keep everything then,
                // rather than emptying the picker over a missing field.
                const assigned = all.filter((sp: any) => sp?.isAssigned);
                setSpaces(all.some((sp: any) => 'isAssigned' in sp) ? assigned : all);
            })
            .catch(() => { /* the picker degrades to "no default room" */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEnabled]);

    /**
     * Patch the visualizer's nested `settings.inline` block. Seeds from
     * defaultInlineEmbedSettings() on first touch so a half-filled block never
     * reaches the SDK — the SDK has its own fallbacks, but a config the vendor
     * can read back in full is easier to reason about than one full of holes.
     */
    const updateInlineSettings = (patch: Record<string, any>) => {
        setIntegration(prev => {
            const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
            const current = nextFeatures.visualizer;
            if (!current) return prev;
            const settings = (current.settings || {}) as VisualizerSettings;
            nextFeatures.visualizer = {
                ...current,
                settings: {
                    ...settings,
                    inline: { ...defaultInlineEmbedSettings(), ...(settings.inline || {}), ...patch },
                } as VisualizerSettings,
            };
            return { ...prev, features: nextFeatures };
        });
    };

    /**
     * The in-gallery panel is a scope of its own, sitting beside the features in
     * the picker above rather than under one of them. It is a second surface the
     * visualizer appears on — a sibling of the button, not a setting of it — and
     * it has no button, no device variant and no placement of the kind the
     * editors below assume, so it takes over the tab when selected.
     */
    const [inlineScope, setInlineScope] = useState(false);
    // The picker's reply arrives on a window listener registered once, so it reads
    // the scope from a ref rather than from a closed-over render's value.
    const inlineScopeRef = useRef(false);

    // Turning the panel off in Features takes its pill away; staying in a scope
    // with no pill would leave the tab blank with no way back.
    useEffect(() => {
        if (!inlineEnabled) { setInlineScope(false); inlineScopeRef.current = false; }
    }, [inlineEnabled]);

    const [activeDevice, setActiveDevice] = useState<'web' | 'mobile'>('web');
    const activeDeviceRef = useRef<'web' | 'mobile'>('web');

    // Which feature's button is currently being edited. Only enabled features are shown
    // in the pill selector. Default to the first enabled one so vendors never land on a
    // disabled/absent feature's button config.
    /*
     * A widget shop has no visualizer button, so it gets no scope for designing
     * one. Offering it would be an editor for something the SDK will not render
     * — the vendor styles a button, saves, and finds nothing on their page.
     * AR and 3D are their own features and keep theirs either way.
     */
    const widgetSurface = (integration.features?.visualizer?.settings as any)?.surface === 'widget'
        || (!((integration.features?.visualizer?.settings as any)?.surface)
            && (integration.features?.visualizer?.settings as VisualizerSettings | undefined)?.inline?.enabled === true);

    const enabledFeatures = (['visualizer', 'ar', 'model3d'] as FeatureKey[])
        .filter(k => !!integration.features?.[k]?.enabled)
        .filter(k => !(k === 'visualizer' && widgetSurface));
    const firstEnabled = (enabledFeatures[0] || 'visualizer') as FeatureKey;

    // Layout mode decides the scope of what's editable on this tab:
    //   - 'buttons': each feature has its own independent button → show a pill
    //                selector for switching between them, edit each feature's
    //                full IntegrationButtonConfig in turn.
    //   - 'menu':    there's only ONE button on the page (the trigger that
    //                opens the feature popover) and it always borrows the
    //                visualizer feature's button config. So pin activeFeature
    //                to 'visualizer' and hide the pill selector; the Button
    //                Design tab effectively becomes a single-target editor
    //                labelled "Menu Trigger Button".
    const isMenuMode = integration.layoutMode === 'menu';
    const initialFeature: FeatureKey = isMenuMode ? 'visualizer' : firstEnabled;
    const [activeFeature, setActiveFeature] = useState<FeatureKey>(initialFeature);
    const activeFeatureRef = useRef<FeatureKey>(initialFeature);

    // Reconcile activeFeature against config changes:
    //   - Flipping to menu mode → snap back to visualizer so the sole editor
    //     target is the trigger button, no matter what was selected before.
    //   - Disabling the current feature in buttons mode → bounce to the first
    //     still-enabled feature to keep the UI from rendering against a
    //     disabled/absent block.
    useEffect(() => {
        if (isMenuMode && activeFeature !== 'visualizer') {
            setActiveFeature('visualizer');
            activeFeatureRef.current = 'visualizer';
            return;
        }
        if (!isMenuMode && !enabledFeatures.includes(activeFeature) && enabledFeatures.length > 0) {
            setActiveFeature(enabledFeatures[0]);
            activeFeatureRef.current = enabledFeatures[0];
        }
        /*
         * A shop whose only feature is the widget has no button to design, so
         * the button editor would open on nothing. Land on the widget instead —
         * it is the only thing on this page with anything to set.
         */
        if (!isMenuMode && enabledFeatures.length === 0 && inlineEnabled && !inlineScopeRef.current) {
            setInlineScope(true);
            inlineScopeRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integration.features, integration.layoutMode]);

    /**
     * Feature-scoped update helper. Every mutation goes through this so the Button
     * Design tab writes to `integration.features[activeFeature].button` (or
     * `.mobileButton`), keeping the legacy top-level `button`/`mobileButton` untouched
     * for the cache-transition shim in handleSave.
     *
     * `device` defaults to the ref-backed activeDevice so postMessage listeners
     * (registered once on mount) always target the latest device selection.
     */
    const updateFeatureButton = (
        feature: FeatureKey,
        device: 'web' | 'mobile',
        patch: Record<string, any>,
    ) => {
        setIntegration(prev => {
            const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
            const block = nextFeatures[feature];
            if (!block) return prev;   // defensive — shouldn't happen post-migration
            if (device === 'web') {
                nextFeatures[feature] = { ...block, button: { ...block.button, ...patch } };
            } else {
                const currentMobile = block.mobileButton || { ...block.button };
                nextFeatures[feature] = {
                    ...block,
                    mobileButton: { ...currentMobile, ...patch } as Partial<IntegrationButtonConfig>,
                };
            }
            return { ...prev, features: nextFeatures };
        });
    };

    const handleDeviceChange = (device: 'web' | 'mobile') => {
        // First switch to mobile for the active feature: seed mobileButton from the
        // feature's web button so vendors start from a known state instead of nothing.
        if (device === 'mobile' && !integration.features?.[activeFeature]?.mobileButton) {
            const webButton = integration.features?.[activeFeature]?.button;
            if (webButton) {
                setIntegration(prev => {
                    const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
                    const block = nextFeatures[activeFeature];
                    if (!block) return prev;
                    nextFeatures[activeFeature] = { ...block, mobileButton: { ...webButton } };
                    return { ...prev, features: nextFeatures };
                });
            }
        }
        setActiveDevice(device);
        activeDeviceRef.current = device;
    };

    const handleFeatureChange = (feature: FeatureKey) => {
        setInlineScope(false);
        inlineScopeRef.current = false;
        setActiveFeature(feature);
        activeFeatureRef.current = feature;
    };

    // Resolve the effective button for the active (feature, device) pair. Mobile always
    // inherits from web via getResolvedMobileButton when no full override exists.
    const activeBlock = integration.features?.[activeFeature];
    const resolvedButton = activeDevice === 'web'
        ? (activeBlock?.button || integration.button)
        : getResolvedMobileButton(
            activeBlock?.button || integration.button,
            activeBlock?.mobileButton || null,
        );

    const updateButton = (key: string, value: any) => {
        updateFeatureButton(activeFeature, activeDevice, { [key]: value });
    };

    /**
     * Open-mode is part of a feature's `settings` rather than its `button`. For the
     * visualizer this is iframe_overlay/new_tab; model3d and AR have their own
     * semantics (modalSize, desktopFallback) edited in the Features card. We only
     * expose openMode editing for the visualizer feature here — other features'
     * open modes are implicit.
     */
    const handleOpenModeChange = (val: 'iframe_overlay' | 'new_tab') => {
        setIntegration(prev => {
            const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
            const block = nextFeatures[activeFeature];
            if (!block) return prev;
            const current = (block.settings as any) || {};
            const patch = activeDevice === 'web' ? { openMode: val } : { mobileOpenMode: val };
            nextFeatures[activeFeature] = { ...block, settings: { ...current, ...patch } };
            return { ...prev, features: nextFeatures };
        });
    };

    /** Reset the active feature's mobile overrides — not the whole integration. */
    const handleResetToWeb = () => {
        setIntegration(prev => {
            const nextFeatures = { ...(prev.features || {}) } as Record<FeatureKey, FeatureBlock>;
            const block = nextFeatures[activeFeature];
            if (!block) return prev;
            nextFeatures[activeFeature] = { ...block, mobileButton: null };
            return { ...prev, features: nextFeatures };
        });
    };

    const activeSettings = (activeBlock?.settings as any) || {};
    const currentOpenMode = activeDevice === 'web'
        ? (activeSettings.openMode || integration.openMode)
        : (activeSettings.mobileOpenMode ?? activeSettings.openMode ?? integration.openMode);

    const btn = resolvedButton;
    // For the picker tab, which asks for the button after this render is gone.
    const btnRef = useRef(btn);
    btnRef.current = btn;

    /**
     * A pick, from wherever it was made.
     *
     * The canvas frames the page and the placer inside it posts here; a page
     * that refuses framing is opened in a tab by the canvas and that tab posts
     * here too. Refs rather than state, so a pick that lands after the vendor
     * has switched feature or device is still written to what is open now.
     */
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            if (e.data?.type === 'ardealy-picker-result' && typeof e.data.selector === 'string' && e.data.selector) {
                // The page picker also says where around the element; the pop-up one does not.
                const position = ['before', 'prepend', 'append', 'after', 'overlay'].includes(e.data.position)
                    ? e.data.position
                    : null;
                // Same picker, two destinations: the widget's gallery while its
                // scope is open, the button's container otherwise.
                if (inlineScopeRef.current) {
                    updateInlineSettings(inlineSettingsFromPick(e.data.selector, position, e.data.gallery));
                } else {
                    updateFeatureButton(
                        activeFeatureRef.current,
                        activeDeviceRef.current,
                        { containerSelector: e.data.selector, ...(position ? { insertPosition: position } : {}) },
                    );
                }
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Empty state — no features enabled yet. They are switched on under Features.
    // Nothing to design only when there is neither a button nor a widget. A
    // widget shop has no buttons by definition, and still has a widget to place.
    if (enabledFeatures.length === 0 && !inlineEnabled) {
        return (
            <p className="text-sm text-muted-foreground">
                {t('INTEGRATION.BUTTON.NO_FEATURES_ENABLED')}
            </p>
        );
    }

    // The words the canvas's placer uses for the same five spots, so the summary
    // here and the label on the page agree.
    const positionLabels: Record<string, string> = {
        before: t('INTEGRATION.PLACER.POS.BEFORE', 'Just before'),
        prepend: t('INTEGRATION.PLACER.POS.PREPEND', 'Inside, at the top'),
        append: t('INTEGRATION.PLACER.POS.APPEND', 'Inside, at the bottom'),
        after: t('INTEGRATION.PLACER.POS.AFTER', 'Just after'),
        overlay: t('INTEGRATION.PLACER.POS.OVERLAY', 'On top of it'),
    };

    // Which surface is being edited: a feature's button (or the menu trigger), or
    // the gallery panel.
    const scopeValue = inlineScope ? 'inline' : isMenuMode ? 'visualizer' : activeFeature;
    const onScopeChange = (value: string) => {
        if (!value) return;
        if (value === 'inline') {
            setInlineScope(true);
            inlineScopeRef.current = true;
        } else {
            handleFeatureChange(value as FeatureKey);
        }
    };
    const scopeLabel = (isMenuMode ? t('INTEGRATION.BUTTON.EDITING_SCOPE') : t('INTEGRATION.BUTTON.FEATURE_PICKER'))
        .replace(/\s*[:：]\s*$/, '');

    return (
        <div className="flex flex-col gap-8">
            {/* Scope. In buttons mode, one item per enabled feature, each switching
                every editor below (placement, builder, picker, canvas). In menu
                mode there is exactly one button on the page — the trigger — and it
                always maps to features.visualizer.button, so there is one item. The
                gallery panel joins either once it is on. */}
            <SettingsField
                label={scopeLabel}
                hint={isMenuMode && !inlineScope ? t('INTEGRATION.BUTTON.MENU_TRIGGER_HINT') : undefined}
            >
                <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    className="w-full flex-wrap"
                    value={scopeValue}
                    onValueChange={onScopeChange}
                >
                    {isMenuMode ? (
                        <ToggleGroupItem value="visualizer" data-testid="feature-pill-menu-trigger" className="flex-1">
                            {t('INTEGRATION.BUTTON.MENU_TRIGGER')}
                        </ToggleGroupItem>
                    ) : (
                        enabledFeatures.map((feature) => (
                            <ToggleGroupItem key={feature} value={feature} data-testid={`feature-pill-${feature}`} className="flex-1">
                                {t(`INTEGRATION.FEATURES.${feature.toUpperCase()}.TITLE`)}
                            </ToggleGroupItem>
                        ))
                    )}
                    {/* Only once the vendor has turned the panel on, in Features —
                        a scope you cannot configure is a dead end. */}
                    {inlineEnabled && (
                        <ToggleGroupItem value="inline" data-testid="feature-pill-inline" className="flex-1">
                            {t('INTEGRATION.FEATURES.VISUALIZER.SURFACE_WIDGET')}
                        </ToggleGroupItem>
                    )}
                </ToggleGroup>
            </SettingsField>

            {/* The button has a mobile variant; the panel does not. */}
            {!inlineScope && (
                <div className="flex items-center justify-between gap-3">
                    <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        value={activeDevice}
                        onValueChange={(v) => v && handleDeviceChange(v as 'web' | 'mobile')}
                    >
                        <ToggleGroupItem value="web" data-testid="device-web">
                            <Monitor className="size-4" />
                            {t('INTEGRATION.DEVICE.WEB')}
                        </ToggleGroupItem>
                        <ToggleGroupItem value="mobile" data-testid="device-mobile">
                            <Smartphone className="size-4" />
                            {t('INTEGRATION.DEVICE.MOBILE')}
                        </ToggleGroupItem>
                    </ToggleGroup>
                    {activeDevice === 'mobile' && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            data-testid="reset-to-web"
                            className="text-muted-foreground"
                            onClick={handleResetToWeb}
                        >
                            {t('INTEGRATION.DEVICE.RESET_TO_WEB')}
                        </Button>
                    )}
                </div>
            )}

            {!inlineScope && (
                <SettingsGroup title={t('INTEGRATION.BUTTON.PLACEMENT')}>
                    {/* Where it is now, in words — it is chosen on the page itself. */}
                    {btn.containerSelector ? (
                        <p className="flex flex-wrap items-center gap-1.5 text-sm text-foreground" data-testid="placement-summary">
                            <span>{positionLabels[btn.insertPosition] || positionLabels.append}</span>
                            <code className="max-w-full truncate rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                                {btn.containerSelector}
                            </code>
                        </p>
                    ) : (
                        <p className="text-sm text-muted-foreground" data-testid="placement-summary">
                            {t('INTEGRATION.BUTTON.NOT_PLACED', 'Not placed yet')}
                        </p>
                    )}


                    {/* For a spot the page cannot show, or a selector the vendor already knows. */}
                    <Collapsible>
                        <CollapsibleTrigger asChild>
                            <Button type="button" variant="ghost" size="sm" className="group -ms-2 text-muted-foreground">
                                <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
                                {t('INTEGRATION.BUTTON.SET_BY_HAND', 'Set it by hand')}
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="flex flex-col gap-4 pt-3">
                            <SettingsField
                                label={t('INTEGRATION.BUTTON.CONTAINER_SELECTOR')}
                                htmlFor="integration-button-selector"
                                hint={t('INTEGRATION.BUTTON.CONTAINER_SELECTOR_HELPER')}
                            >
                                <Input
                                    id="integration-button-selector"
                                    className="font-mono text-xs"
                                    placeholder={t('INTEGRATION.BUTTON.CONTAINER_SELECTOR_PLACEHOLDER')}
                                    value={btn.containerSelector}
                                    onChange={(e) => updateButton('containerSelector', e.target.value)}
                                />
                            </SettingsField>
                            <SettingsField label={t('INTEGRATION.BUTTON.INSERT_POSITION')}>
                                <Select
                                    value={btn.insertPosition}
                                    onValueChange={(value) => updateButton('insertPosition', value)}
                                >
                                    <SelectTrigger className="w-full" data-testid="insert-position-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(['before', 'prepend', 'append', 'after', 'overlay'] as const).map((pos) => (
                                            <SelectItem key={pos} value={pos}>{positionLabels[pos]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </SettingsField>
                        </CollapsibleContent>
                    </Collapsible>
                </SettingsGroup>
            )}

            {/*
                The canvas: the page the thing lands on, and the thing itself,
                one above the other.

                They were two tabs, and a tab is a claim that you only need one
                of them at a time. Placing and designing are the same sitting —
                the label you just typed is what you want to see sitting in the
                gallery, and the spot you just picked is what makes the label
                the right length. Behind a tab, every such check costs a click
                and a rebuild of whichever view you left.

                Both surfaces work this way: a button and a widget differ in
                what is drawn, not in how a vendor goes about it.
            */}
            <CanvasPortal>
                <div className="flex flex-col gap-6">
                    <section className="flex flex-col gap-2">
                        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t('INTEGRATION.PLACER.VIEW_PAGE', 'On your page')}
                        </h3>
                        <LivePagePlacer
                            domains={(integration.allowedDomains || []).filter(
                                (d: string) => !!integration.domainStatus?.[d.toLowerCase().replace(/^www\./, '')]?.connectedAt,
                            )}
                            exampleUrl={integration.matchConfig?._exampleUrl}
                            device={activeDevice}
                            button={inlineScope ? null : btn}
                            panel={inlineScope
                                ? {
                                    aspectRatio: inlineSettings.aspectRatio,
                                    label: inlineSettings.label,
                                    insertIndex: inlineSettings.insertIndex,
                                    minHeight: inlineSettings.minHeight,
                                    panelCSS: inlineSettings.panelCSS,
                                }
                                : null}
                            selector={inlineScope ? inlineSettings.containerSelector : btn.containerSelector}
                            position={inlineScope ? inlineSettings.insertPosition : btn.insertPosition}
                        />
                    </section>

                    <section className="flex flex-col gap-2">
                        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {inlineScope
                                ? t('INTEGRATION.PLACER.VIEW_WIDGET', 'Widget design')
                                : t('INTEGRATION.PLACER.VIEW_DESIGN', 'Button design')}
                        </h3>
                        {inlineScope ? (
                            <WidgetBuilder settings={inlineSettings} onChange={updateInlineSettings} t={t} />
                        ) : (
                            <div className="rounded-xl border bg-background p-5">
                                <ButtonBuilder
                                    button={btn}
                                    onButtonChange={updateButton}
                                    t={t}
                                    openMode={currentOpenMode}
                                    onOpenModeChange={handleOpenModeChange}
                                />
                            </div>
                        )}
                    </section>
                </div>
            </CanvasPortal>

            {inlineScope && (
                <InlineEmbedSettingsPanel
                    settings={inlineSettings}
                    onChange={updateInlineSettings}
                    spaces={spaces}
                    t={t}
                />
            )}
        </div>
    );
};

// Fixed self-hiding save notice — replaces the MUI Snackbar+Alert pair
const SaveNotice = ({ open, message, severity, onClose }: {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
    onClose: () => void;
}) => {
    useEffect(() => {
        if (!open) return;
        const timer = window.setTimeout(onClose, 3000);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    return (
        <div
            role="alert"
            className={cn(
                'animate-in fade-in slide-in-from-bottom-4 fixed bottom-[calc(1.5rem+var(--support-fab-clearance,0px))] end-6 z-50 flex items-center gap-3 border px-3 py-2 text-sm shadow-sm',
                severity === 'success'
                    ? 'border-success/40 bg-success/10 text-success'
                    : 'border-destructive/40 bg-destructive/10 text-destructive',
            )}
        >
            <span>{message}</span>
            <button
                type="button"
                onClick={onClose}
                className="text-muted-foreground transition-colors hover:text-foreground"
            >
                <X className="size-4" />
            </button>
        </div>
    );
};

// ==================== MAIN PAGE ====================
type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';
const AUTOSAVE_DELAY_MS = 2000;

/**
 * One page component, two places.
 *
 * `website` is Home › Integration: the domains, whether each is connected, and
 * the snippet — general, since every feature runs from that snippet.
 * `visualizer` is Visualizer › Integration: how the visualizer finds products,
 * which of its features are on, and what its button looks like. Both edit the
 * same stored integration through the same load and autosave, which is why they
 * are one component rather than two copies of it.
 */
export const IntegrationPage = ({ inTab = false, scope = 'visualizer', section }: {
    inTab?: boolean;
    scope?: 'website' | 'visualizer';
    /** Render one section, bare, inside the Visualizer studio's settings panel. */
    section?: 'matching' | 'features' | 'button';
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const settingsAPI = useSettingsApi();
    const [tabName, setTabName] = useTabParam(INTEGRATION_TABS);
    const activeTab = INTEGRATION_TABS.indexOf(tabName);
    const setActiveTab = (index: number) => setTabName(INTEGRATION_TABS[index] ?? 'matching');
    const [integration, setIntegration] = useState<IntegrationConfig>(DEFAULT_INTEGRATION);
    const [vendorAlias, setVendorAlias] = useState('');
    const [saveState, setSaveState] = useState<SaveState>('idle');
    // Inside the studio, this autosave reports to the one status in its top bar.
    const autosaveReporter = useContext(AutosaveReporterContext);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(true);
    const savedSnapshotRef = useRef<string>('');
    // Autosave bookkeeping. The save runs from timers and unmount, so it reads the
    // latest edits through a ref rather than a render's closure.
    const integrationRef = useRef(integration);
    integrationRef.current = integration;
    const saveTimerRef = useRef<number | null>(null);
    const inFlightRef = useRef(false);
    const mountedRef = useRef(true);

    // The visualizer's tabs wait for a connected website, which is set up on Home.
    // Not for a feature being on: switching the visualizer on is one of those tabs.
    const isSetupComplete = isWebsiteConnected(integration);

    // Deep merge loaded integration with defaults to avoid losing nested keys
    const mergeIntegration = (saved: any, primaryColor?: string): IntegrationConfig => {
        const savedButton = saved?.button || {};
        // Migrate old single padding to H/V split
        if (savedButton.padding && !savedButton.paddingH && !savedButton.paddingV) {
            const parts = savedButton.padding.replace(/px/g, '').trim().split(/\s+/);
            savedButton.paddingV = parts[0] || '12';
            savedButton.paddingH = parts[1] || parts[0] || '24';
            delete savedButton.padding;
        }
        const merged: IntegrationConfig = {
            ...DEFAULT_INTEGRATION,
            ...saved,
            matchConfig: { ...DEFAULT_INTEGRATION.matchConfig, ...(saved?.matchConfig || {}) },
            button: { ...DEFAULT_INTEGRATION.button, ...savedButton },
            mobileButton: saved?.mobileButton ?? null,
            mobileOpenMode: saved?.mobileOpenMode,
        };
        // Default button bg to vendor primary color if not explicitly set
        if (!saved?.button?.backgroundColor && primaryColor) {
            merged.button.backgroundColor = primaryColor;
        }
        // Sanitize label HTML on load to strip any dangerous content
        if (merged.button.label) {
            merged.button.label = sanitizeButtonLabel(merged.button.label);
        }
        if (merged.mobileButton?.label) {
            merged.mobileButton.label = sanitizeButtonLabel(merged.mobileButton.label);
        }
        // v1 → v2 migration: populate `features` map from the legacy button/openMode.
        // Idempotent — already-v2 configs are returned as-is.
        return migrateIntegrationToV2(merged);
    };

    const refreshConfig = useCallback(async () => {
        try {
            const config = await settingsAPI.findConfig();
            setVendorAlias(config.alias || '');
            if (config.integration) {
                const merged = mergeIntegration(config.integration, config.primaryColor);
                setIntegration(merged);
                savedSnapshotRef.current = JSON.stringify(merged);
            } else {
                // Nothing stored yet: the defaults are the baseline, so opening the
                // page does not autosave them before the vendor changes anything.
                savedSnapshotRef.current = JSON.stringify(integrationRef.current);
            }
        } catch (error) {
            console.error('Error loading integration config:', error);
        }
    }, []);

    // Load config on mount
    useEffect(() => {
        refreshConfig().finally(() => setLoading(false));
    }, []);

    // Saves whatever the vendor has now. Runs a few seconds after the last edit
    // (there is no Save button — it was too easy to leave without pressing it),
    // on Retry, and when the page unmounts with edits still waiting.
    const saveNow = async () => {
        if (saveTimerRef.current !== null) {
            window.clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        // No baseline until the stored config has loaded — never save over it with defaults.
        if (!savedSnapshotRef.current) return;
        const integration = integrationRef.current;
        const sentJson = JSON.stringify(integration);
        if (sentJson === savedSnapshotRef.current) return;
        // One save at a time; edits made meanwhile are picked up when it lands.
        if (inFlightRef.current) return;

        inFlightRef.current = true;
        if (mountedRef.current) setSaveState('saving');
        try {
            // Sanitize labels before persisting to backend
            const sanitizedIntegration: IntegrationConfig = {
                ...integration,
                button: {
                    ...integration.button,
                    label: sanitizeButtonLabel(integration.button.label || ''),
                },
                ...(integration.mobileButton ? {
                    mobileButton: {
                        ...integration.mobileButton,
                        label: sanitizeButtonLabel(integration.mobileButton.label || ''),
                    },
                } : {}),
            };

            // v1 cache-transition shim — mirror v2 features.visualizer.* state back into
            // the legacy top-level isEnabled/button/openMode fields so any v1 SDK that
            // is still cached in a vendor's CDN continues to behave correctly. Once v1
            // SDK URLs are deprecated (~90 days post-rollout), this shim can drop.
            //
            // `isEnabled` is also the master gate the backend's /config endpoint checks
            // — so it must flip true when ANY feature is enabled, not strictly mirror
            // Visualizer. Otherwise a vendor who enables only AR or only 3D silently
            // gets a 404 "Integration not enabled" from /config.
            if (sanitizedIntegration.schemaVersion === 2 && sanitizedIntegration.features) {
                const visualizerBlock = sanitizedIntegration.features.visualizer;
                sanitizedIntegration.isEnabled = Object.values(sanitizedIntegration.features).some(
                    (f: FeatureBlock | undefined) => !!f?.enabled,
                );
                if (visualizerBlock) {
                    sanitizedIntegration.button = { ...visualizerBlock.button };
                    const vs = visualizerBlock.settings as VisualizerSettings | undefined;
                    // v1 SDKs still served from CDN caches know only the two original
                    // modes, so inline_embed is mirrored down to the overlay: an old
                    // SDK then degrades to a working full-screen visualizer rather
                    // than an open mode it cannot interpret.
                    if (vs?.openMode) sanitizedIntegration.openMode = toLegacyOpenMode(vs.openMode);
                    if (visualizerBlock.mobileButton) sanitizedIntegration.mobileButton = visualizerBlock.mobileButton;
                    if (vs?.mobileOpenMode) sanitizedIntegration.mobileOpenMode = toLegacyOpenMode(vs.mobileOpenMode);
                }
            }

            const response = await settingsAPI.updateConfig({ integration: sanitizedIntegration });
            const editedMeanwhile = JSON.stringify(integrationRef.current) !== sentJson;
            if (response?.integration && !editedMeanwhile && mountedRef.current) {
                // Sync with what the server persisted — it also carries connection
                // status the SDK recorded since this page loaded.
                const merged = mergeIntegration(response.integration);
                setIntegration(merged);
                savedSnapshotRef.current = JSON.stringify(merged);
            } else {
                // Never overwrite edits typed while the request was out; they are
                // still unsaved and go in the follow-up save below.
                savedSnapshotRef.current = sentJson;
            }
            if (mountedRef.current) setSaveState('saved');
        } catch (error) {
            console.error('Error saving integration config:', error);
            if (mountedRef.current) {
                setSaveState('error');
                setSnackbar({ open: true, message: t('INTEGRATION.SAVE_FAILED'), severity: 'error' });
            }
            return;
        } finally {
            inFlightRef.current = false;
        }

        // Edits arrived during the request and their own timer is not waiting.
        if (JSON.stringify(integrationRef.current) !== savedSnapshotRef.current && saveTimerRef.current === null) {
            saveNow();
        }
    };

    // Debounced autosave on every change, across all tabs (they share this state).
    useEffect(() => {
        if (loading) return;
        if (JSON.stringify(integration) === savedSnapshotRef.current) return;
        setSaveState('pending');
        if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = window.setTimeout(() => {
            saveTimerRef.current = null;
            saveNow();
        }, AUTOSAVE_DELAY_MS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integration, loading]);

    // Leaving the page (or its tab) must not drop the last few seconds of edits.
    useEffect(() => {
        mountedRef.current = true; // re-set: StrictMode runs this cleanup once on mount
        const hasUnsaved = () =>
            inFlightRef.current || JSON.stringify(integrationRef.current) !== savedSnapshotRef.current;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!hasUnsaved()) return;
            saveNow();
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            mountedRef.current = false;
            saveNow();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!autosaveReporter || !section) return;
        autosaveReporter.report(saveState, () => { void saveNow(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveState, section]);

    const connectFirst = (
        <Alert className={section ? undefined : 'mb-6'}>
            <Lock />
            <AlertDescription className="flex flex-col items-start gap-3">
                <span>{t('INTEGRATION.VISUALIZER.CONNECT_FIRST', 'Connect your website first — add your domain and install the snippet on Home › Website.')}</span>
                <Button type="button" variant="outline" size="sm" onClick={() => navigate('/home/integration')}>
                    {t('INTEGRATION.VISUALIZER.CONNECT_CTA', 'Connect your website')}
                </Button>
            </AlertDescription>
        </Alert>
    );

    if (section) {
        if (loading) {
            return (
                <div className="flex min-h-[240px] items-center justify-center">
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
            );
        }
        return (
            <div className="flex flex-col gap-6">
                {/* Saving is shown in the studio's top bar, with the other sections'. */}
                {!isSetupComplete ? connectFirst : (
                    <>
                        {section === 'matching' && (
                            <ProductMatchingTab integration={integration} setIntegration={setIntegration} t={t} />
                        )}
                        {section === 'features' && (
                            <div className="flex flex-col gap-8">
                                <FeaturesTab integration={integration} setIntegration={setIntegration} t={t} />
                                <StandaloneTriggerCard t={t} />
                            </div>
                        )}
                        {section === 'button' && (
                            <ButtonDesignTab integration={integration} setIntegration={setIntegration} t={t} />
                        )}
                    </>
                )}
                <SaveNotice
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                />
            </div>
        );
    }

    if (loading) {
        return (
            <PageContainer hideTopNav={inTab} title={t('INTEGRATION.TITLE')}>
                <div className="flex min-h-[400px] items-center justify-center">
                    <Loader2 className="size-8 animate-spin text-foreground" />
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            hideTopNav={inTab}
            title={t('INTEGRATION.TITLE')}
            breadcrumbs={scope === 'website'
                ? [{ label: t('WORKSPACE.HOME', 'Home') }, { label: t('SIDEBAR.HOME.WEBSITE', 'Website') }]
                : [{ label: t('SIDEBAR.VISUALIZER.TITLE') }, { label: t('SIDEBAR.CUSTOMIZATION.INTEGRATION') }]}
        >
            {/* Header — title on the left, autosave status on the right */}
            <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
                        {scope === 'website' ? t('INTEGRATION.WEBSITE.TITLE', 'Your website') : t('INTEGRATION.VISUALIZER.TITLE', 'Visualizer on your website')}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {scope === 'website'
                            ? t('INTEGRATION.WEBSITE.SUBTITLE', 'Connect your website once. The visualizer, the Shopping Assistant and everything after them run from this snippet.')
                            : t('INTEGRATION.VISUALIZER.SUBTITLE', 'How the visualizer finds your products, which of its features are on, and what its button looks like.')}
                    </p>
                </div>
                <div className="flex h-9 shrink-0 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                    {(saveState === 'pending' || saveState === 'saving') && (
                        <>
                            <Loader2 className="size-3.5 animate-spin" />
                            {t('INTEGRATION.SAVING')}
                        </>
                    )}
                    {saveState === 'saved' && (
                        <>
                            <Check className="size-3.5" />
                            {t('INTEGRATION.AUTOSAVED')}
                        </>
                    )}
                    {saveState === 'error' && (
                        <>
                            <span className="text-destructive">{t('INTEGRATION.SAVE_FAILED')}</span>
                            <Button type="button" variant="outline" className="h-9" onClick={() => saveNow()}>
                                <RefreshCw className="size-3.5" />
                                {t('INTEGRATION.RETRY')}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {scope === 'website' ? (
                <SetupTab
                    integration={integration}
                    setIntegration={setIntegration}
                    vendorAlias={vendorAlias}
                    t={t}
                />
            ) : (
                <>
                    {/* Until the website is connected there is nothing for these settings to act on. */}
                    {!isSetupComplete && connectFirst}

                    {/* Tabs — segmented pills; locked until the website is connected */}
                    <div className="mb-6 inline-block">
                        <Tabs
                            value={String(activeTab)}
                            onValueChange={(key) => {
                                if (!isSetupComplete) return;
                                setActiveTab(Number(key));
                            }}
                        >
                            <TabsList>
                                <TabsTrigger value="0" className="px-3">
                                    {!isSetupComplete && <Lock className="size-3" />}
                                    {t('INTEGRATION.MATCHING.TITLE')}
                                </TabsTrigger>
                                <TabsTrigger value="1" className="px-3">
                                    {!isSetupComplete && <Lock className="size-3" />}
                                    {t('INTEGRATION.FEATURES.TITLE')}
                                </TabsTrigger>
                                <TabsTrigger value="2" className="px-3">
                                    {!isSetupComplete && <Lock className="size-3" />}
                                    {t('INTEGRATION.BUTTON.TITLE')}
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    {activeTab === 0 && isSetupComplete && (
                        <ProductMatchingTab
                            integration={integration}
                            setIntegration={setIntegration}
                            t={t}
                        />
                    )}
                    {activeTab === 1 && isSetupComplete && (
                        <div className="flex flex-col gap-6">
                            <FeaturesTab
                                integration={integration}
                                setIntegration={setIntegration}
                                t={t}
                            />
                            <StandaloneTriggerCard t={t} />
                        </div>
                    )}
                    {activeTab === 2 && isSetupComplete && (
                        <ButtonDesignTab
                            integration={integration}
                            setIntegration={setIntegration}
                            t={t}
                        />
                    )}
                </>
            )}

            <SaveNotice
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </PageContainer>
    );
};
